import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  collection,
  getDocs,
  deleteField,
  addDoc,
  serverTimestamp,
  query,
  onSnapshot,
  arrayUnion,
} from "firebase/firestore";
import Confetti from "react-confetti";

// --- Imports จากไฟล์ที่เราแยกไว้ ---
import { db } from "../../config/firebase";
import { GAME_PHASES, DEFAULT_GAME_MODES } from "../../config/constants";
import { ALL_CLUE_CARDS, ROLE_CARD_DESIGNS, AVATARS } from "../../config/data";
import { SOUND_LIBRARY, controlBgm } from "../../utils/soundUtils";
import useWindowWidth from "../../hooks/useWindowWidth";
import usePrevious from "../../hooks/usePrevious";

// --- Components ---
import HoldToConfirmButton from "../common/HoldToConfirmButton";
import StatusSwitcher from "../common/StatusSwitcher";
import SettingCounterSwitch from "../common/SettingCounterSwitch";
import ToggleSwitch from "../common/ToggleSwitch";
import FallingLeaves from "../common/FallingLeaves";

// --- Modals ---
import ClueModal from "../modals/ClueModal";
import RoleModal from "../modals/RoleModal";
import EvidenceHistoryModal from "../modals/EvidenceHistoryModal";
import ShareModal from "../modals/ShareModal";
import LeaveRoomConfirmModal from "../modals/LeaveRoomConfirmModal";
import HowToPlayModal from "../modals/HowToPlayModal";

const GoogleIcon = () => (
  <svg className="w-3.5 h-3.5 inline-block align-middle shrink-0" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.85z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.85c.87-2.6 3.3-4.53 6.17-4.53z"
    />
  </svg>
);

// Fisher-Yates Shuffle Algorithm (สุ่มสมบูรณ์แบบไม่ลำเอียง)
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const ROLE_NAMES_TH = {
  Murderer: "ฆาตกร",
  Assistant: "ผู้ช่วยฆาตกร",
  Detective: "นักสืบ",
  Agent: "เอเจนต์",
  Coroner: "แพทย์นิติเวช",
  Innocent: "ผู้บริสุทธิ์",
  AssistantRepentant: "ผู้ช่วยฆาตกรกลับใจ",
};

export default function GameScreen({
  gameState,
  userId,
  me,
  gameId,
  onLeaveRoom,
  soundSettings,
  onOpenSettings,
  bgmAudioRef,
  isVibrationMuted,
  isVoiceMuted,
}) {
  const audioCache = useRef({});
  const [zoomedClue, setZoomedClue] = useState(null);
  const [isRoleModalVisible, setRoleModalVisible] = useState(false);
  const [isHistoryModalVisible, setHistoryModalVisible] = useState(false);
  const logContainerRef = useRef(null);
  const [isHowToPlayVisible, setIsHowToPlayVisible] = useState(false);
  const [isShareModalVisible, setShareModalVisible] = useState(false);
  const [isCardFlipped, setCardFlipped] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [showVictimEffect, setShowVictimEffect] = useState(false);
  const [showEliminatedEffect, setShowEliminatedEffect] = useState(false);
  const [showAssistantEffect, setShowAssistantEffect] = useState(false);
  const [showDetectiveEffect, setShowDetectiveEffect] = useState(false);
  const [isLeaveConfirmVisible, setIsLeaveConfirmVisible] = useState(false);
  const [vignetteEffect, setVignetteEffect] = useState(null);
  const [lobbyPlayers, setLobbyPlayers] = useState({});
  const [showTimesUpEffect, setShowTimesUpEffect] = useState(false);
  const [showMurdererEffect, setShowMurdererEffect] = useState(false);
  const [showAgentEffect, setShowAgentEffect] = useState(false);
  const [showCoronerEffect, setShowCoronerEffect] = useState(false);
  const [showGlow, setShowGlow] = useState(false);
  const [ghostsList, setGhostsList] = useState([]);

  const windowWidth = useWindowWidth();
  const tickSoundRef = useRef(null);
  const prevPhase = usePrevious(gameState.phase);
  const gameDocRef = useMemo(
    () => doc(db, "murderMysteryGames", gameId),
    [gameId]
  );

  const hasRevealed = gameState.revealedRoles?.[userId];
  const prevHasRevealed = usePrevious(hasRevealed);

  const voteCounts = useMemo(() => {
    if (!gameState.votes) return {};
    const counts = {};
    for (const voterId in gameState.votes) {
      const targetId = gameState.votes[voterId];
      if (targetId) {
        counts[targetId] = (counts[targetId] || 0) + 1;
      }
    }
    return counts;
  }, [gameState.votes]);

  const playVibration = useCallback(
    (pattern) => {
      if (isVibrationMuted) return;
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    },
    [isVibrationMuted]
  );

  useEffect(() => {
    const soundsToPreload = [
      { name: "card_flip", src: "/sounds/card_flip.mp3" },
      { name: "paper_flip", src: "/sounds/paper_flip.mp3" },
      { name: "pin_thud", src: "/sounds/pin_thud.mp3" },
      { name: "night_begins_vo", src: "/sounds/vo_night_begins.mp3" },
      { name: "day_begins_vo", src: "/sounds/vo_day_begins.mp3" },
      { name: "murderer_kill_vo", src: "/sounds/vo_murderer_kill.mp3" },
      { name: "agent_turn_vo", src: "/sounds/vo_agent_turn.mp3" },
      { name: "agent_turn", src: "/sounds/agent_turn.mp3" },
    ];

    soundsToPreload.forEach((sound) => {
      const audio = new Audio(sound.src);
      audio.load();
      audioCache.current[sound.src] = audio;
      if (sound.name) {
        audioCache.current[sound.name] = audio;
      }
    });
  }, []);

  // --- SMART PRELOAD SYSTEM ---
  const preloadImages = useCallback((urls) => {
    urls.forEach((url) => {
      if (!url) return;
      const img = new Image();
      img.src = url;
    });
  }, []);

  // ป้องกันการเลื่อนพื้นหลังเมื่อมีโมดอลเปิด (Prevent body scroll when modals are open)
  useEffect(() => {
    const isAnyModalOpen =
      isHowToPlayVisible ||
      isShareModalVisible ||
      isLeaveConfirmVisible ||
      isRoleModalVisible ||
      isHistoryModalVisible ||
      !!zoomedClue;

    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [
    isHowToPlayVisible,
    isShareModalVisible,
    isLeaveConfirmVisible,
    isRoleModalVisible,
    isHistoryModalVisible,
    zoomedClue,
  ]);

  // 1. Critical Preload (Roles, Avatars, Card Back)
  useEffect(() => {
    const criticalImages = [
      "/images/role_cards/card_back.png",
      ...Object.values(ROLE_CARD_DESIGNS).flat(),
      ...AVATARS,
    ];
    preloadImages(criticalImages);
  }, [preloadImages]);

  // 2. Clue Preload (Active Clues Only)
  useEffect(() => {
    const cluesToLoad = new Set();

    // 2.1 Current Round Clues
    if (gameState.currentClues) {
      gameState.currentClues.forEach(c => cluesToLoad.add(c.url));
    }

    // 2.2 Selected True/Fake Clues
    if (gameState.trueClues) {
      gameState.trueClues.forEach(c => cluesToLoad.add(c.clueUrl));
    }
    if (gameState.fakeClues) {
      gameState.fakeClues.forEach(c => cluesToLoad.add(c.clueUrl));
    }

    // 2.3 Evidence Board
    if (gameState.evidenceBoard) {
      gameState.evidenceBoard.forEach(record => {
        if (record.trueClues) record.trueClues.forEach(c => cluesToLoad.add(c.clueUrl));
        if (record.fakeClues) record.fakeClues.forEach(c => cluesToLoad.add(c.clueUrl));
      });
    }

    if (cluesToLoad.size > 0) {
      preloadImages(Array.from(cluesToLoad));
    }
  }, [
    gameState.currentClues,
    gameState.trueClues,
    gameState.fakeClues,
    gameState.evidenceBoard,
    preloadImages
  ]);

  const playSoundQueue = useCallback(
    (soundConfig, soundIndex = 0) => {
      if (gameId === "preview_mode") return;

      let configList = [];
      if (Array.isArray(soundConfig)) {
        configList = soundConfig.map((snd) => ({
          src: snd,
          category: "ambience",
        }));
      } else if (Array.isArray(soundConfig.sounds)) {
        configList = soundConfig.sounds;
      } else if (typeof soundConfig === "object" && soundConfig.sounds) {
        const sounds = Array.isArray(soundConfig.sounds)
          ? soundConfig.sounds
          : [soundConfig.sounds];
        configList = sounds.map((snd) => ({
          src: snd,
          category: soundConfig.category || "ui",
        }));
      } else if (typeof soundConfig === "object" && soundConfig.src) {
        configList = [soundConfig];
      } else {
        return;
      }

      if (soundIndex >= configList.length) return;

      const currentSound = configList[soundIndex];
      let { src, category } = currentSound;

      if (SOUND_LIBRARY[src]) {
        src = SOUND_LIBRARY[src];
      }

      const playNext = () => {
        playSoundQueue({ sounds: configList }, soundIndex + 1);
      };

      let isMuted = false;
      let shouldPlaySilent = false;

      if (category === "ui" && soundSettings.isUiMuted) isMuted = true;
      if (category === "ambience" && soundSettings.isAmbienceMuted)
        isMuted = true;
      if (category === "voice" && isVoiceMuted) {
        isMuted = true;
        shouldPlaySilent = true;
      }

      if (isMuted && !shouldPlaySilent) {
        playNext();
        return;
      }

      let audio = audioCache.current[src];
      if (!audio) {
        audio = new Audio(src);
        audioCache.current[src] = audio;
      }

      audio.currentTime = 0;
      audio.loop = false;
      audio.muted = shouldPlaySilent;
      audio.onended = playNext;
      audio.play().catch((e) => {
        console.warn(`Cannot play ${src}:`, e);
        playNext();
      });
    },
    [gameId, soundSettings, isVoiceMuted]
  );

  // --- Game Logic Functions (Copied from original) ---
  const handleMurder = useCallback(
    async (targetId, killerId = userId) => {
      const { players } = gameState;
      const newMurders = [{ killerId: killerId, victimId: targetId }];
      const victimIds = [targetId];

      const futurePlayers = JSON.parse(JSON.stringify(players));
      victimIds.forEach((vid) => {
        if (futurePlayers[vid]) futurePlayers[vid].isAlive = false;
      });
      const futureAlivePlayers = Object.values(futurePlayers).filter(
        (p) => p.isAlive
      );
      const aliveMurderersCount = futureAlivePlayers.filter(
        (p) => p.role === "Murderer"
      ).length;
      const goodTeamCount = futureAlivePlayers.filter(
        (p) => p.role === "Innocent" || p.role === "Detective" || p.role === "Agent" || p.role === "Coroner"
      ).length;

      if (goodTeamCount <= aliveMurderersCount) {
        const batch = writeBatch(db);
        const logsToAdd = [];
        victimIds.forEach((vid) => {
          const playerDocRef = doc(
            db,
            "murderMysteryGames",
            gameId,
            "players",
            vid
          );
          const updateData = { isAlive: false };
          if (players[vid]?.role === "Assistant" && players[killerId]?.role === "Murderer") {
            updateData.role = "AssistantRepentant";
            logsToAdd.push(
              `📢 [ผู้ช่วยฆาตกร] ${players[vid]?.name} ถูกสังหารโดยฆาตกร! จึงเปลี่ยนฝ่ายมาเป็นฝ่ายผู้บริสุทธิ์ 🕊️`
            );
          }
          batch.update(playerDocRef, updateData);
        });
        const updateFields = {
          phase: GAME_PHASES.GAME_OVER,
          winner: "Murderer",
          winReason:
            "ฝ่ายฆาตกรชนะ เพราะจำนวนผู้บริสุทธิ์เหลือน้อยเกินไป",
          murders: newMurders,
          victimIds: victimIds,
        };
        if (logsToAdd.length > 0) {
          updateFields.gameLogs = arrayUnion(...logsToAdd);
        }
        batch.update(gameDocRef, updateFields);
        await batch.commit();
        return;
      }

      await updateDoc(gameDocRef, {
        phase: GAME_PHASES.VICTIM_ACTION,
        murders: newMurders,
        victimIds: victimIds,
      });
    },
    [gameState, userId, gameId, gameDocRef]
  );

  // --- Ghost Feature ---
  useEffect(() => {
    if (!gameId) return;
    const ghostsRef = collection(db, "murderMysteryGames", gameId, "ghosts");
    // Removed orderBy/limit to avoid index requirements (which might cause silent failures)
    // We will filter heavily on client side instead.
    const q = query(ghostsRef);

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          const data = change.doc.data();
          const newGhost = { id: change.doc.id, ...data };

          // Check timestamp. Allow if createdAt is null (local pending write) OR created recently
          // Using 60s tolerance to handle potential client clock skew
          const createdAtMillis = data.createdAt ? data.createdAt.toMillis() : Date.now();
          const isRecent = Math.abs(Date.now() - createdAtMillis) < 60000;

          if (isRecent) {
            setGhostsList((prev) => {
              // Prevent duplicate processing if id already exists
              if (prev.some(g => g.id === newGhost.id)) return prev;
              return [...prev, { ...newGhost, left: Math.random() * 80 + 10 }];
            });
            playSoundQueue({ src: "/sounds/pop.mp3", category: "ui" });
          }
        }
      });
    });

    return () => unsubscribe();
  }, [gameId, playSoundQueue]);

  const handleSendGhost = useCallback(async () => {
    if (!gameId || !userId) return;
    try {
      const ghostsRef = collection(db, "murderMysteryGames", gameId, "ghosts");
      await addDoc(ghostsRef, {
        senderId: userId,
        createdAt: serverTimestamp(),
        emoji: "👻"
      });
      // Feedback vibration
      playVibration(50);
    } catch (error) {
      console.error("Error sending ghost:", error);
    }
  }, [gameId, userId, playVibration]);

  const handleGhostAnimationEnd = (id) => {
    setGhostsList((prev) => prev.filter((g) => g.id !== id));
  };



  const assignRolesAndStart = useCallback(async () => {
    const { players, specialRoles } = gameState;
    const playerIds = Object.keys(players);
    const playerCount = playerIds.length;
    if (playerCount < 3) return;

    const rolePool = [];
    const {
      assistants = 0,
      detectives = 0,
      extraMurderers = 0,
      agents = 0,
      coroners = 0,
    } = specialRoles || {};

    rolePool.push("Murderer");
    if (playerCount >= 9)
      for (let i = 0; i < Math.min(extraMurderers, 1); i++)
        rolePool.push("Murderer");
    if (playerCount >= 8) {
      for (let i = 0; i < Math.min(assistants, 2); i++)
        rolePool.push("Assistant");
      for (let i = 0; i < Math.min(detectives, 1); i++)
        rolePool.push("Detective");
      for (let i = 0; i < Math.min(coroners, 1); i++)
        rolePool.push("Coroner");
      for (let i = 0; i < Math.min(agents, 1); i++)
        rolePool.push("Agent");
    } else if (playerCount >= 5) {
      for (let i = 0; i < Math.min(assistants, 1); i++)
        rolePool.push("Assistant");
      for (let i = 0; i < Math.min(detectives, 1); i++)
        rolePool.push("Detective");
      for (let i = 0; i < Math.min(coroners, 1); i++)
        rolePool.push("Coroner");
      for (let i = 0; i < Math.min(agents, 1); i++)
        rolePool.push("Agent");
    } else if (playerCount >= 4) {
      for (let i = 0; i < Math.min(agents, 1); i++)
        rolePool.push("Agent");
    }
    while (rolePool.length < playerCount) {
      rolePool.push("Innocent");
    }

    const shuffledRoles = shuffleArray(rolePool);
    const shuffledDesigns = {
      Murderer: shuffleArray(ROLE_CARD_DESIGNS.Murderer),
      Assistant: shuffleArray(ROLE_CARD_DESIGNS.Assistant),
      Detective: shuffleArray(ROLE_CARD_DESIGNS.Detective),
      Agent: shuffleArray(ROLE_CARD_DESIGNS.Agent),
      Coroner: shuffleArray(ROLE_CARD_DESIGNS.Coroner),
      Innocent: shuffleArray(ROLE_CARD_DESIGNS.Innocent),
    };
    const designCounters = {
      Murderer: 0,
      Assistant: 0,
      Detective: 0,
      Agent: 0,
      Coroner: 0,
      Innocent: 0,
    };
    const newMurdererIds = [];
    const batch = writeBatch(db);
    playerIds.forEach((id, index) => {
      const role = shuffledRoles[index];
      const designPool = shuffledDesigns[role];
      const designIndex = designCounters[role] % designPool.length;
      const roleCardImage = designPool[designIndex];
      designCounters[role]++;
      const playerDocRef = doc(db, "murderMysteryGames", gameId, "players", id);
      batch.update(playerDocRef, { role: role, roleCardImage: roleCardImage });
      if (role === "Murderer") {
        newMurdererIds.push(id);
      }
    });
    batch.update(gameDocRef, {
      murdererId: newMurdererIds[0] || null,
      phase: GAME_PHASES.ROLE_REVEAL,
      revealedRoles: {},
      gameLogs: ["📢 คดีฆาตกรรมปริศนาเริ่มต้นขึ้นแล้ว! ค้นหาฆาตกรที่แฝงตัวอยู่ท่ามกลางพวกเรา"],
      writtenLogs: {},
    });
    await batch.commit();
  }, [gameState, gameId, gameDocRef]);

  const prepareFirstRound = useCallback(async () => {
    const shuffledClues = shuffleArray(ALL_CLUE_CARDS);
    const newClues = shuffledClues.slice(0, 9);
    const playersSnapshot = await getDocs(
      collection(db, "murderMysteryGames", gameId, "players")
    );
    const batch = writeBatch(db);
    playersSnapshot.forEach((playerDoc) => {
      batch.update(playerDoc.ref, { isReady: false });
    });
    batch.update(gameDocRef, {
      phase: GAME_PHASES.CLUE_REVEAL,
      currentClues: newClues,
      round: 1,
      readyPlayers: {},
      countdown: null,
      usedClueIds: [],
      silencedPlayerIds: [],
      agentConfirmations: {},
    });
    await batch.commit();
  }, [gameDocRef, gameId]);

  const calculateVotesAndProceed = useCallback(async () => {
    if (gameId === "preview_mode") return;
    const gameSnap = await getDoc(gameDocRef);
    if (!gameSnap.exists() || gameSnap.data().phase !== GAME_PHASES.VOTING)
      return;

    const currentVotes = gameSnap.data().votes || {};
    const voteCounts = {};
    Object.values(currentVotes).forEach((votedId) => {
      if (votedId) voteCounts[votedId] = (voteCounts[votedId] || 0) + 1;
    });

    let eliminatedPlayerId = null,
      maxVotes = 0,
      tie = false;
    for (const pid in voteCounts) {
      if (voteCounts[pid] > maxVotes) {
        maxVotes = voteCounts[pid];
        eliminatedPlayerId = pid;
        tie = false;
      } else if (voteCounts[pid] === maxVotes) {
        tie = true;
      }
    }
    if (tie) eliminatedPlayerId = null;

    await updateDoc(gameDocRef, {
      phase: GAME_PHASES.VOTE_RESULT,
      eliminatedPlayerId,
    });
  }, [gameDocRef, gameId]);

  const proceedToNextPhase = useCallback(
    async (currentPhase) => {
      const { players } = gameState;
      let nextPhase = "";
      let updateData = {};
      const alivePlayers = Object.values(players).filter((p) => p.isAlive);
      const hasAssistants = alivePlayers.some((p) => p.role === "Assistant");
      const hasDetectives = alivePlayers.some((p) => p.role === "Detective");

      switch (currentPhase) {
        case GAME_PHASES.VICTIM_ACTION:
          if (hasAssistants) nextPhase = GAME_PHASES.ASSISTANT_CONFIRM;
          else if (hasDetectives) nextPhase = GAME_PHASES.DETECTIVE_CONFIRM;
          else nextPhase = GAME_PHASES.MURDERER_COVERUP;

          if (nextPhase === GAME_PHASES.MURDERER_COVERUP) {
            const leadMurdererId =
              Object.keys(players).find(
                (pId) =>
                  players[pId].isAlive && players[pId].role === "Murderer"
              ) || null;
            updateData.leadMurdererId = leadMurdererId;
          }
          break;
        case GAME_PHASES.ASSISTANT_CONFIRM:
          if (hasDetectives) nextPhase = GAME_PHASES.DETECTIVE_CONFIRM;
          else nextPhase = GAME_PHASES.MURDERER_COVERUP;
          break;
        case GAME_PHASES.DETECTIVE_CONFIRM:
          nextPhase = GAME_PHASES.MURDERER_COVERUP;
          break;
        case GAME_PHASES.AGENT_ACTION:
          nextPhase = GAME_PHASES.DAY_TRANSITION;
          break;
        default:
          return;
      }
      updateData.phase = nextPhase;
      updateData.confirmations = {};
      await updateDoc(gameDocRef, updateData);
    },
    [gameState, gameDocRef]
  );

  const handleUpdateSpecialRoles = useCallback(
    async (role, change) => {
      const { specialRoles, players } = gameState;
      const currentCount = specialRoles[role] || 0;
      const newCount = currentCount + change;
      if (newCount < 0) return;

      const playerCount = Object.keys(players || {}).length;
      const updates = { [`specialRoles.${role}`]: newCount };

      // กฎสมดุล: ผู้เล่น 5-6 คน ห้ามมีนักสืบและแพทย์นิติเวชพร้อมกัน
      if (playerCount === 5 || playerCount === 6) {
        if (role === "detectives" && newCount > 0) {
          updates["specialRoles.coroners"] = 0;
        } else if (role === "coroners" && newCount > 0) {
          updates["specialRoles.detectives"] = 0;
        }
      }

      await updateDoc(gameDocRef, updates);
    },
    [gameState, gameDocRef]
  );

  const handleGameModeChange = useCallback(
    async (mode, valueOrUpdater) => {
      const currentVal = (gameState.gameModes || DEFAULT_GAME_MODES)[mode];
      const newValue =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(currentVal)
          : valueOrUpdater;
      if (newValue === currentVal) return;
      await updateDoc(gameDocRef, { [`gameModes.${mode}`]: newValue });
    },
    [gameDocRef, gameState]
  );

  const handleStartGame = useCallback(async () => {
    playSoundQueue({ src: "/sounds/button_click.mp3", category: "ui" });
    const { players } = gameState;
    if (Object.keys(players).length < 3) return;
    await updateDoc(gameDocRef, {
      phase: GAME_PHASES.PRE_GAME_COUNTDOWN,
      countdown: 5,
    });
  }, [gameDocRef, gameState, playSoundQueue]);

  const handlePlayAgain = useCallback(async () => {
    playSoundQueue({ src: "/sounds/button_click.mp3", category: "ui" });
    const { players } = gameState;
    if (Object.keys(players).length < 3) {
      alert("มีผู้เล่นไม่เพียงพอที่จะเริ่มเกมใหม่");
      return;
    }
    const batch = writeBatch(db);
    batch.update(gameDocRef, {
      phase: GAME_PHASES.PRE_GAME_COUNTDOWN,
      countdown: 5,
      round: 0,
      evidenceBoard: [],
      murders: [],
      victimIds: [],
      trueClues: [],
      fakeClues: [],
      votes: {},
      eliminatedPlayerId: null,
      winner: null,
      winReason: null,
      confirmations: {},
      revealedRoles: {},
      publiclyRevealedRoles: {},
      murdererId: null,
      usedClueIds: [],
      readyPlayers: {},
      silencedPlayerIds: [],
      agentConfirmations: {},
      coronerQuery: null,
      gameLogs: [],
      writtenLogs: {},
    });
    Object.keys(players).forEach((playerId) => {
      const playerDocRef = doc(
        db,
        "murderMysteryGames",
        gameId,
        "players",
        playerId
      );
      batch.update(playerDocRef, {
        role: null,
        isAlive: true,
        roleCardImage: null,
      });
    });
    await batch.commit();
  }, [gameDocRef, gameState, gameId, playSoundQueue]);

  const handleReturnToLobby = useCallback(async () => {
    playSoundQueue({ src: "/sounds/button_click.mp3", category: "ui" });
    const { players } = gameState;
    const batch = writeBatch(db);
    batch.update(gameDocRef, {
      phase: GAME_PHASES.LOBBY,
      countdown: null,
      round: 0,
      evidenceBoard: [],
      murders: [],
      victimIds: [],
      trueClues: [],
      fakeClues: [],
      votes: {},
      eliminatedPlayerId: null,
      winner: null,
      winReason: null,
      confirmations: {},
      revealedRoles: {},
      publiclyRevealedRoles: {},
      murdererId: null,
      usedClueIds: [],
      readyPlayers: {},
      silencedPlayerIds: [],
      agentConfirmations: {},
      coronerQuery: null,
      gameLogs: [],
      writtenLogs: {},
    });
    Object.keys(players).forEach((playerId) => {
      const playerDocRef = doc(
        db,
        "murderMysteryGames",
        gameId,
        "players",
        playerId
      );
      batch.update(playerDocRef, {
        role: null,
        isAlive: true,
        roleCardImage: null,
      });
    });
    await batch.commit();
  }, [gameDocRef, gameState, gameId, playSoundQueue]);

  const handleReady = useCallback(async () => {
    await updateDoc(gameDocRef, { [`readyPlayers.${userId}`]: true });
  }, [gameDocRef, userId]);

  const handleCancelReady = useCallback(async () => {
    await updateDoc(gameDocRef, { [`readyPlayers.${userId}`]: deleteField() });
  }, [gameDocRef, userId]);

  const handleRevealRole = useCallback(async () => {
    const { revealedRoles } = gameState;
    const alreadyRevealed = revealedRoles?.[userId];
    if (alreadyRevealed) return;
    playSoundQueue({ sounds: "card_flip", category: "ui" });
    setCardFlipped(true);
    playVibration(100);
    await updateDoc(gameDocRef, { [`revealedRoles.${userId}`]: true });
  }, [gameDocRef, userId, gameState, playSoundQueue, playVibration]);

  const handleSelectTrueClue = useCallback(
    async (clue) => {
      const { trueClues, victimIds, gameModes, players } = gameState;
      const newTrueClues = [
        ...(trueClues || []),
        {
          clueId: clue.id,
          clueName: clue.name,
          clueUrl: clue.url,
          selectedBy: userId,
        },
      ];
      if (newTrueClues.length >= victimIds.length) {
        const batch = writeBatch(db);
        const logsToAdd = [];
        victimIds.forEach((vid) => {
          const playerDocRef = doc(
            db,
            "murderMysteryGames",
            gameId,
            "players",
            vid
          );
          
          const murderRecord = (gameState.murders || []).find((m) => m.victimId === vid);
          const killerId = murderRecord ? murderRecord.killerId : null;
          const killerRole = killerId ? players[killerId]?.role : null;
          
          const updateData = { isAlive: false };
          let isRepentant = false;
          if (players[vid]?.role === "Assistant" && killerRole === "Murderer") {
            updateData.role = "AssistantRepentant";
            isRepentant = true;
            logsToAdd.push(
              `📢 [ผู้ช่วยฆาตกร] ${players[vid]?.name} ถูกสังหารโดยฆาตกร! จึงเปลี่ยนฝ่ายมาเป็นฝ่ายผู้บริสุทธิ์ 🕊️`
            );
          }
          batch.update(playerDocRef, updateData);

          if (gameModes?.isBeginnerMode) {
            const victimRole = players[vid]?.role;
            if (victimRole) {
              const revealedRole = isRepentant ? "AssistantRepentant" : victimRole;
              batch.update(gameDocRef, {
                [`publiclyRevealedRoles.${vid}`]: revealedRole,
              });
            }
          }
        });
        const updateFields = { trueClues: newTrueClues };
        if (logsToAdd.length > 0) {
          updateFields.gameLogs = arrayUnion(...logsToAdd);
        }
        batch.update(gameDocRef, updateFields);
        await batch.commit();
        await proceedToNextPhase(GAME_PHASES.VICTIM_ACTION);
      } else {
        await updateDoc(gameDocRef, { trueClues: newTrueClues });
      }
    },
    [gameState, userId, gameId, gameDocRef, proceedToNextPhase]
  );

  const handleAssistantConfirm = useCallback(async () => {
    const { players, confirmations } = gameState;
    const aliveAssistants = Object.values(players).filter(
      (p) => p.isAlive && p.role === "Assistant"
    );
    const newConfirmations = { ...(confirmations || {}), [userId]: true };
    const confirmedAssistants = aliveAssistants.filter(
      (a) => newConfirmations[a.id]
    );
    if (confirmedAssistants.length >= aliveAssistants.length) {
      await proceedToNextPhase(GAME_PHASES.ASSISTANT_CONFIRM);
    } else {
      await updateDoc(gameDocRef, { confirmations: newConfirmations });
    }
  }, [gameState, userId, gameDocRef, proceedToNextPhase]);

  const handleDetectiveConfirm = useCallback(async () => {
    await proceedToNextPhase(GAME_PHASES.DETECTIVE_CONFIRM);
  }, [proceedToNextPhase]);

  const handleAgentSilence = useCallback(
    async (targetId) => {
      const { players, agentConfirmations } = gameState;
      const aliveAgents = Object.values(players).filter(
        (p) => p.isAlive && p.role === "Agent"
      );
      const newConfirmations = { ...(agentConfirmations || {}), [userId]: targetId };
      const confirmedCount = aliveAgents.filter((a) => newConfirmations[a.id] !== undefined).length;
      const newSilencedIds = Object.values(newConfirmations).filter(Boolean);
      if (confirmedCount >= aliveAgents.length) {
        await updateDoc(gameDocRef, {
          agentConfirmations: newConfirmations,
          silencedPlayerIds: newSilencedIds,
          phase: GAME_PHASES.DAY_TRANSITION,
        });
      } else {
        await updateDoc(gameDocRef, { agentConfirmations: newConfirmations });
      }
    },
    [gameState, userId, gameDocRef]
  );

  const handleCoronerQuery = useCallback(
    async (clue) => {
      await updateDoc(gameDocRef, {
        coronerQuery: {
          clueId: clue.id,
          clueName: clue.name,
          clueUrl: clue.url,
          response: null,
        },
      });
    },
    [gameDocRef]
  );

  const handleCoronerResponse = useCallback(
    async (answer) => {
      const { players, victimIds } = gameState;
      const hasAgentToAct = Object.values(players).some(
        (p) =>
          p.role === "Agent" &&
          p.isAlive &&
          !(victimIds || []).includes(p.id)
      );
      const nextPhase = hasAgentToAct
        ? GAME_PHASES.AGENT_ACTION
        : GAME_PHASES.DAY_TRANSITION;

      await updateDoc(gameDocRef, {
        "coronerQuery.response": answer,
        phase: nextPhase,
      });
    },
    [gameState, gameDocRef]
  );

  const handleSelectFakeClue = useCallback(
    async (clue) => {
      const { fakeClues, trueClues } = gameState;
      const newFakeClues = [
        ...(fakeClues || []),
        {
          clueId: clue.id,
          clueName: clue.name,
          clueUrl: clue.url,
          selectedBy: userId,
        },
      ];
      const requiredFakeClues = 2;
      if (newFakeClues.length >= requiredFakeClues) {
        const combined = [...(trueClues || []), ...newFakeClues];
        const shuffled = shuffleArray(combined);
        // เช็คว่ามีเอเจนต์หรือแพทย์นิติเวชที่ยังมีชีวิตอยู่ และไม่ได้ถูกสังหารรอบนี้
        const { players, victimIds } = gameState;
        const hasAgentToAct = Object.values(players).some(
          (p) =>
            p.role === "Agent" &&
            p.isAlive &&
            !(victimIds || []).includes(p.id)
        );
        const hasCoronerToAct = Object.values(players).some(
          (p) =>
            p.role === "Coroner" &&
            p.isAlive &&
            !(victimIds || []).includes(p.id)
        );
        const nextPhase = hasCoronerToAct
          ? GAME_PHASES.CORONER_ACTION
          : hasAgentToAct
            ? GAME_PHASES.AGENT_ACTION
            : GAME_PHASES.DAY_TRANSITION;

        await updateDoc(gameDocRef, {
          phase: nextPhase,
          fakeClues: newFakeClues,
          shuffledClues: shuffled,
          countdown: null,
          agentConfirmations: {},
          coronerQuery: null,
        });
      } else {
        await updateDoc(gameDocRef, { fakeClues: newFakeClues });
      }
    },
    [gameState, gameDocRef, userId]
  );

  const handleStartVoting = useCallback(
    () => updateDoc(gameDocRef, { phase: GAME_PHASES.VOTING, countdown: 20 }),
    [gameDocRef]
  );

  const handleVote = useCallback(
    (targetId) => {
      updateDoc(gameDocRef, { [`votes.${userId}`]: targetId });
      playVibration(50);
    },
    [gameDocRef, userId, playVibration]
  );

  const handleProceedAfterVote = useCallback(async () => {
    const {
      players,
      hostId,
      eliminatedPlayerId,
      victimIds,
      trueClues,
      fakeClues,
      usedClueIds,
      round,
      evidenceBoard,
      gameModes,
    } = gameState;
    if (hostId !== userId) return;

    const victimNames = (victimIds || [])
      .map((vid) => players[vid]?.name || "Unknown")
      .join(", ");
    const evidenceToBoard = {
      round: round,
      trueClues: trueClues || [],
      fakeClues: fakeClues || [],
      victim: victimNames,
    };
    const newEvidenceBoard = [...(evidenceBoard || []), evidenceToBoard];
    const currentRoundClueIds = [
      ...(trueClues || []).map((c) => c.clueId),
      ...(fakeClues || []).map((c) => c.clueId),
    ];
    const newUsedClueIds = [...(usedClueIds || []), ...currentRoundClueIds];
    const batch = writeBatch(db);

    if (eliminatedPlayerId) {
      batch.update(
        doc(db, "murderMysteryGames", gameId, "players", eliminatedPlayerId),
        { isAlive: false }
      );
      if (gameModes?.isBeginnerMode) {
        const eliminatedPlayerRole = players[eliminatedPlayerId]?.role;
        if (eliminatedPlayerRole)
          batch.update(gameDocRef, {
            [`publiclyRevealedRoles.${eliminatedPlayerId}`]:
              eliminatedPlayerRole,
          });
      }
    }
    await batch.commit();

    const playersSnapshot = await getDocs(
      collection(db, "murderMysteryGames", gameId, "players")
    );
    let currentPlayers = {};
    playersSnapshot.forEach((doc) => (currentPlayers[doc.id] = doc.data()));
    const alivePlayers = Object.values(currentPlayers).filter((p) => p.isAlive);
    const aliveMurderers = alivePlayers.filter((p) => p.role === "Murderer");
    const goodTeamCount = alivePlayers.filter(
      (p) => p.role === "Innocent" || p.role === "Detective" || p.role === "Agent" || p.role === "Coroner"
    ).length;

    let voteLogText = "";
    if (eliminatedPlayerId) {
      const pName = players[eliminatedPlayerId]?.name || "ไม่ทราบชื่อ";
      const pRole = players[eliminatedPlayerId]?.role;
      if (gameModes?.isBeginnerMode && pRole) {
        voteLogText = `🗳️ กลางวันรอบที่ ${round}: ผู้เล่น [${pName}] ได้รับคะแนนโหวตสูงสุดและถูกขับไล่ออกจากหมู่บ้าน (บทบาท: ${ROLE_NAMES_TH[pRole] || pRole})`;
      } else {
        voteLogText = `🗳️ กลางวันรอบที่ ${round}: ผู้เล่น [${pName}] ได้รับคะแนนโหวตสูงสุดและถูกขับไล่ออกจากหมู่บ้าน`;
      }
    } else {
      voteLogText = `🗳️ กลางวันรอบที่ ${round}: เสียงโหวตกึ่งหนึ่งเสมอกัน ไม่มีใครถูกขับไล่ออกจากหมู่บ้านในรอบนี้`;
    }

    if (aliveMurderers.length === 0) {
      await updateDoc(gameDocRef, {
        phase: GAME_PHASES.GAME_OVER,
        winner: "Innocent",
        winReason: "ฝ่ายผู้บริสุทธิ์ชนะ เพราะกำจัดฆาตกรได้ทั้งหมด",
        evidenceBoard: newEvidenceBoard,
        usedClueIds: newUsedClueIds,
        gameLogs: arrayUnion(
          voteLogText,
          "🏆 จบคดี: ฝ่ายผู้บริสุทธิ์ชนะคดี! เพราะกำจัดฆาตกรตัวจริงได้สำเร็จ"
        ),
      });
      return;
    }
    if (goodTeamCount <= aliveMurderers.length) {
      await updateDoc(gameDocRef, {
        phase: GAME_PHASES.GAME_OVER,
        winner: "Murderer",
        winReason: "ฝ่ายฆาตกรชนะ เพราะจำนวนผู้บริสุทธิ์เหลือน้อยเกินไป",
        evidenceBoard: newEvidenceBoard,
        usedClueIds: newUsedClueIds,
        gameLogs: arrayUnion(
          voteLogText,
          "🏆 จบคดี: ฝ่ายฆาตกรชนะคดี! เพราะผู้บริสุทธิ์ถูกขับไล่จนเหลือจำนวนน้อยเกินไป"
        ),
      });
      return;
    }

    const nextRoundNumber = round + 1;
    const availableClues = ALL_CLUE_CARDS.filter(
      (card) => !newUsedClueIds.includes(card.id)
    );
    const shuffledAvailableClues = shuffleArray(availableClues);
    const newCluesForNextRound = shuffledAvailableClues.slice(0, 9);
    const resetBatch = writeBatch(db);
    resetBatch.update(gameDocRef, {
      phase: GAME_PHASES.CLUE_REVEAL,
      round: nextRoundNumber,
      currentClues: newCluesForNextRound,
      victimIds: [],
      trueClues: [],
      fakeClues: [],
      votes: {},
      eliminatedPlayerId: null,
      murders: [],
      confirmations: {},
      shuffledClues: [],
      evidenceBoard: newEvidenceBoard,
      usedClueIds: newUsedClueIds,
      readyPlayers: {},
      silencedPlayerIds: [],
      agentConfirmations: {},
      gameLogs: arrayUnion(voteLogText),
    });
    await resetBatch.commit();
  }, [userId, gameId, gameDocRef, gameState]);

  // --- Effects ---
  useEffect(() => {
    if (
      gameState.phase === GAME_PHASES.LOBBY ||
      gameState.phase === GAME_PHASES.PRE_GAME_COUNTDOWN
    ) {
      setCardFlipped(false);
    }
  }, [gameState]);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [gameState.gameLogs]);

  useEffect(() => {
    const { phase, countdown } = gameState;
    const shouldPlay =
      (phase === GAME_PHASES.DISCUSSION || phase === GAME_PHASES.VOTING) &&
      countdown !== null &&
      countdown <= 5 &&
      countdown > 0;

    if (shouldPlay && !soundSettings.isAmbienceMuted) {
      if (!tickSoundRef.current) {
        try {
          tickSoundRef.current = new Audio("/sounds/clock_tick.mp3");
          tickSoundRef.current.loop = true;
          tickSoundRef.current
            .play()
            .catch((e) => console.error("Error playing tick sound:", e));
        } catch (e) {
          console.error("Could not create Audio object", e);
        }
      }
    } else {
      if (tickSoundRef.current) {
        tickSoundRef.current.pause();
        tickSoundRef.current = null;
      }
    }

    if (
      phase === GAME_PHASES.PRE_GAME_COUNTDOWN &&
      countdown !== null &&
      countdown > 0 &&
      prevPhase === phase
    ) {
      playVibration(150);
    }

    if (
      (phase === GAME_PHASES.DISCUSSION || phase === GAME_PHASES.VOTING) &&
      countdown === 0 &&
      prevPhase === phase
    ) {
      playVibration([150, 75, 150]);
      setShowTimesUpEffect(true);
      setTimeout(() => setShowTimesUpEffect(false), 3000);
    }

    return () => {
      if (tickSoundRef.current) {
        tickSoundRef.current.pause();
        tickSoundRef.current = null;
      }
    };
  }, [gameState, playVibration, prevPhase, soundSettings]);

  useEffect(() => {
    let targetSong = null;
    if (gameState.phase === GAME_PHASES.LOBBY) {
      targetSong = "/sounds/bgm_menu.mp3";
    }
    controlBgm(bgmAudioRef, targetSong, soundSettings.isBgmMuted);
  }, [gameState.phase, soundSettings.isBgmMuted, bgmAudioRef]);

  useEffect(() => {
    if (
      gameState.phase === GAME_PHASES.MURDERER_ACTION &&
      me?.role === "Murderer"
    )
      setVignetteEffect("murderer");
    else if (
      gameState.phase === GAME_PHASES.VICTIM_ACTION &&
      (gameState.victimIds || []).includes(userId)
    )
      setVignetteEffect("victim");
    else if (
      gameState.phase === GAME_PHASES.ASSISTANT_CONFIRM &&
      me?.role === "Assistant"
    )
      setVignetteEffect("assistant");
    else if (
      gameState.phase === GAME_PHASES.DETECTIVE_CONFIRM &&
      me?.role === "Detective"
    )
      setVignetteEffect("detective");
    else if (
      gameState.phase === GAME_PHASES.AGENT_ACTION &&
      me?.role === "Agent"
    )
      setVignetteEffect("agent");
    else setVignetteEffect(null);
  }, [gameState.phase, me?.role, gameState.victimIds, userId]);

  useEffect(() => {
    if (hasRevealed && !prevHasRevealed) {
      const timer = setTimeout(() => setShowGlow(true), 700);
      return () => clearTimeout(timer);
    }
    if (!hasRevealed) setShowGlow(false);
  }, [hasRevealed, prevHasRevealed]);

  useEffect(() => {
    const newPlayers = gameState.players;
    setLobbyPlayers((currentLobbyPlayers) => {
      const updatedPlayers = { ...currentLobbyPlayers };
      const departingIds = Object.keys(currentLobbyPlayers).filter(
        (id) => !newPlayers[id]
      );
      Object.keys(newPlayers).forEach((id) => {
        if (!currentLobbyPlayers[id])
          updatedPlayers[id] = { ...newPlayers[id], status: "entering" };
        else updatedPlayers[id] = { ...newPlayers[id], status: "idle" };
      });
      departingIds.forEach((id) => {
        updatedPlayers[id] = { ...currentLobbyPlayers[id], status: "exiting" };
      });
      return updatedPlayers;
    });
  }, [gameState.players]);

  useEffect(() => {
    const hasExitingPlayers = Object.values(lobbyPlayers).some(
      (p) => p.status === "exiting"
    );
    if (hasExitingPlayers) {
      const timer = setTimeout(() => {
        setLobbyPlayers((current) => {
          const final = { ...current };
          Object.keys(final).forEach((id) => {
            if (final[id].status === "exiting") delete final[id];
          });
          return final;
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [lobbyPlayers]);

  useEffect(() => {
    if (prevPhase === gameState.phase) return;
    const soundsForLiving = {
      [GAME_PHASES.MURDERER_ACTION]: [
        { src: "/sounds/vo_murderer_kill.mp3", category: "voice" },
      ],
      [GAME_PHASES.ASSISTANT_CONFIRM]: [
        { src: "/sounds/assistant_turn.mp3", category: "ambience" },
        { src: "/sounds/vo_assistant_turn.mp3", category: "voice" },
      ],
      [GAME_PHASES.DETECTIVE_CONFIRM]: [
        { src: "/sounds/detective_turn.mp3", category: "ambience" },
        { src: "/sounds/vo_detective_turn.mp3", category: "voice" },
      ],
      [GAME_PHASES.CORONER_ACTION]: [
        { src: "/sounds/detective_turn.mp3", category: "ambience" },
        { src: "/sounds/vo_detective_turn.mp3", category: "voice" },
      ],
      [GAME_PHASES.AGENT_ACTION]: [
        { src: "/sounds/agent_turn.mp3", category: "ambience" },
        { src: "/sounds/vo_agent_turn.mp3", category: "voice" },
      ],
      [GAME_PHASES.MURDERER_COVERUP]: [
        { src: "/sounds/church_bell.mp3", category: "ambience" },
        { src: "/sounds/vo_murderer_coverup.mp3", category: "voice" },
      ],
    };
    const soundsForAll = {
      [GAME_PHASES.NIGHT_TRANSITION]: [
        { src: "/sounds/night_begins.mp3", category: "ambience" },
        { src: "/sounds/vo_night_begins.mp3", category: "voice" },
      ],
      [GAME_PHASES.DAY_TRANSITION]: [
        { src: "/sounds/rooster_crow.mp3", category: "ambience" },
        { src: "/sounds/vo_day_begins.mp3", category: "voice" },
      ],
      [GAME_PHASES.GAME_OVER]:
        gameState.winner === "Innocent"
          ? [
            { src: "/sounds/vo_innocent_win.mp3", category: "voice" },
            { src: "/sounds/innocent_win.mp3", category: "ambience" },
          ]
          : [
            { src: "/sounds/vo_murderer_win.mp3", category: "voice" },
            { src: "/sounds/murderer_win.mp3", category: "ambience" },
          ],
    };
    if (soundsForLiving[gameState.phase])
      playSoundQueue({ sounds: soundsForLiving[gameState.phase] });
    if (soundsForAll[gameState.phase])
      playSoundQueue({ sounds: soundsForAll[gameState.phase] });
  }, [gameState, me, playSoundQueue, prevPhase]);

  useEffect(() => {
    if (
      gameState.phase === GAME_PHASES.VICTIM_ACTION &&
      prevPhase !== GAME_PHASES.VICTIM_ACTION
    ) {
      if (gameState.victimIds && gameState.victimIds.includes(userId)) {
        playSoundQueue({
          sounds: [
            { src: "/sounds/pain_scream.mp3", category: "ambience" },
            { src: "/sounds/vo_victim_choose_clue.mp3", category: "voice" },
          ],
          category: "ambience",
        });
        setShowVictimEffect(true);
        setTimeout(() => setShowVictimEffect(false), 3000);
        playVibration([700, 300, 700, 300, 700, 300, 700]);
      }
    }
  }, [gameState, prevPhase, userId, playSoundQueue, playVibration]);

  useEffect(() => {
    if (
      gameState.phase === GAME_PHASES.VOTE_RESULT &&
      prevPhase !== GAME_PHASES.VOTE_RESULT &&
      gameState.eliminatedPlayerId === userId
    ) {
      playSoundQueue({
        sounds: [{ src: "/sounds/voted_out.mp3", category: "ambience" }],
        category: "ambience",
      });
      setShowEliminatedEffect(true);
      setTimeout(() => setShowEliminatedEffect(false), 4000);
      playVibration([400, 150, 400]);
    }
  }, [gameState, prevPhase, userId, playSoundQueue, playVibration]);

  useEffect(() => {
    if (prevPhase === gameState.phase) return;
    if (
      gameState.phase === GAME_PHASES.ASSISTANT_CONFIRM &&
      me?.role === "Assistant"
    ) {
      setShowAssistantEffect(true);
      setTimeout(() => setShowAssistantEffect(false), 4000);
    }
    if (
      gameState.phase === GAME_PHASES.DETECTIVE_CONFIRM &&
      me?.role === "Detective"
    ) {
      setShowDetectiveEffect(true);
      setTimeout(() => setShowDetectiveEffect(false), 4000);
    }
    if (
      gameState.phase === GAME_PHASES.MURDERER_ACTION &&
      me?.role === "Murderer"
    ) {
      setShowMurdererEffect(true);
      setTimeout(() => setShowMurdererEffect(false), 4000);
    }
    if (
      gameState.phase === GAME_PHASES.CORONER_ACTION &&
      me?.role === "Coroner"
    ) {
      setShowCoronerEffect(true);
      setTimeout(() => setShowCoronerEffect(false), 4000);
    }
    if (
      gameState.phase === GAME_PHASES.AGENT_ACTION &&
      me?.role === "Agent"
    ) {
      setShowAgentEffect(true);
      setTimeout(() => setShowAgentEffect(false), 4000);
    }
  }, [gameState, me, prevPhase, playSoundQueue]);

  useEffect(() => {
    if (gameId === "preview_mode" || gameState.hostId !== userId) return;
    const { phase, countdown } = gameState;
    let timer;

    if (phase === GAME_PHASES.NIGHT_TRANSITION) {
      timer = setTimeout(async () => {
        const gameSnap = await getDoc(gameDocRef);
        const currentGameState = gameSnap.data();
        const playersSnapshot = await getDocs(
          collection(db, "murderMysteryGames", gameId, "players")
        );
        let currentPlayers = {};
        playersSnapshot.forEach((doc) => (currentPlayers[doc.id] = doc.data()));

        const alivePlayers = Object.entries(currentPlayers).filter(
          ([id, p]) => p.isAlive
        );
        const aliveMurdererIds = alivePlayers
          .filter(([, p]) => p.role === "Murderer")
          .map(([id]) => id);

        if (currentGameState.gameModes?.isRandomKillMode) {
          const potentialVictims = alivePlayers.filter(
            ([, p]) => p.role !== "Murderer" && p.role !== "Assistant"
          );
          if (potentialVictims.length > 0) {
            const randomVictimIndex = Math.floor(
              Math.random() * potentialVictims.length
            );
            const victimId = potentialVictims[randomVictimIndex][0];
            const victimIds = [victimId];
            const newMurders = [
              { killerId: aliveMurdererIds[0] || null, victimId: victimId },
            ];
            // Logic to check game over (simplified for brevity, assume handleMurder like logic handles updates)
            // Note: In full implementation, replicate the checkWin logic here or refactor into a shared function.
            // For now, assuming direct update for Random Kill mode transition:
            await updateDoc(gameDocRef, {
              phase: GAME_PHASES.VICTIM_ACTION,
              murders: newMurders,
              victimIds: victimIds,
              killersForThisRound: aliveMurdererIds,
            });
          } else {
            updateDoc(gameDocRef, { phase: GAME_PHASES.DAY_TRANSITION });
          }
        } else {
          const leadMurdererId =
            alivePlayers.find(([id, p]) => p.role === "Murderer")?.[0] || null;
          updateDoc(gameDocRef, {
            phase: GAME_PHASES.MURDERER_ACTION,
            leadMurdererId: leadMurdererId,
            killersForThisRound: aliveMurdererIds,
            silencedPlayerIds: [],
            agentConfirmations: {},
          });
        }
      }, 8000);
    } else if (phase === GAME_PHASES.DAY_TRANSITION) {
      const logKey = `night_${gameState.round || 1}`;
      if (!gameState.writtenLogs?.[logKey]) {
        const victimNames = (gameState.victimIds || [])
          .map((vid) => gameState.players?.[vid]?.name || "ไม่ทราบชื่อ")
          .join(", ");
        const silencedNames = (gameState.silencedPlayerIds || [])
          .map((sid) => gameState.players?.[sid]?.name || "ไม่ทราบชื่อ")
          .join(", ");

        const newLogs = [];
        if (victimNames) {
          newLogs.push(`💀 คืนที่ ${gameState.round || 1}: ผู้เล่น [${victimNames}] ถูกพบเป็นศพอย่างมีปริศนา`);
        } else {
          newLogs.push(`🕊️ คืนที่ ${gameState.round || 1}: คืนนี้ผ่านไปอย่างเงียบสงบ ไม่มีใครเสียชีวิต`);
        }
        if (silencedNames) {
          newLogs.push(`🤐 คืนที่ ${gameState.round || 1}: ผู้เล่น [${silencedNames}] ถูกเอเจนต์ปิดปาก ห้ามร่วมวิเคราะห์ในวันนี้`);
        }

        updateDoc(gameDocRef, {
          gameLogs: arrayUnion(...newLogs),
          [`writtenLogs.${logKey}`]: true,
        });
      }

      const discussionTime = gameState.gameModes?.discussionTime || 60;
      timer = setTimeout(() => {
        updateDoc(gameDocRef, {
          phase: GAME_PHASES.DISCUSSION,
          countdown: discussionTime,
        });
      }, 5000);
    }

    if (countdown > 0) {
      timer = setTimeout(() => {
        updateDoc(gameDocRef, { countdown: countdown - 1 });
      }, 1000);
    } else if (countdown === 0) {
      if (phase === GAME_PHASES.PRE_GAME_COUNTDOWN) {
        prepareFirstRound();
      } else if (phase === GAME_PHASES.DISCUSSION) {
        timer = setTimeout(() => {
          updateDoc(gameDocRef, { phase: GAME_PHASES.VOTING, countdown: 20 });
        }, 3000);
      } else if (phase === GAME_PHASES.VOTING) {
        timer = setTimeout(() => {
          calculateVotesAndProceed();
        }, 3000);
      }
    }
    return () => clearTimeout(timer);
  }, [
    gameState,
    userId,
    gameId,
    gameDocRef,
    prepareFirstRound,
    calculateVotesAndProceed,
  ]);

  useEffect(() => {
    const { phase, hostId, players, murdererId } = gameState;
    if (
      gameId === "preview_mode" ||
      hostId !== userId ||
      !gameState ||
      phase !== GAME_PHASES.CLUE_REVEAL
    )
      return;
    if (!players || Object.keys(players).length === 0) return;
    const alivePlayers = Object.values(players).filter((p) => p.isAlive);
    const readyCount = Object.keys(gameState.readyPlayers || {}).length;
    if (alivePlayers.length > 0 && readyCount === alivePlayers.length) {
      if (!murdererId) assignRolesAndStart();
      else updateDoc(gameDocRef, { phase: GAME_PHASES.NIGHT_TRANSITION });
    }
  }, [gameState, userId, gameId, assignRolesAndStart, gameDocRef]);

  useEffect(() => {
    const { phase, hostId, players, revealedRoles } = gameState;
    if (
      gameId === "preview_mode" ||
      hostId !== userId ||
      !gameState ||
      phase !== GAME_PHASES.ROLE_REVEAL
    )
      return;
    if (!players || Object.keys(players).length === 0) return;
    const alivePlayersCount = Object.values(players).filter(
      (p) => p.isAlive
    ).length;
    const revealedCount = Object.keys(revealedRoles || {}).length;
    if (alivePlayersCount > 0 && revealedCount === alivePlayersCount) {
      const timer = setTimeout(
        () => updateDoc(gameDocRef, { phase: GAME_PHASES.NIGHT_TRANSITION }),
        1500
      );
      return () => clearTimeout(timer);
    }
  }, [gameState, userId, gameId, gameDocRef]);

  useEffect(() => {
    const { phase, hostId, players, votes } = gameState;
    if (gameId === "preview_mode") return;
    if (phase === GAME_PHASES.VOTING && hostId === userId) {
      const alivePlayerIds = Object.keys(players).filter(
        (id) => players[id].isAlive
      );
      if (Object.keys(votes || {}).length === alivePlayerIds.length) {
        calculateVotesAndProceed();
      }
    }
  }, [gameState, userId, gameId, calculateVotesAndProceed]);

  useEffect(() => {
    const checkWinAfterDeath = async () => {
      const { players, hostId } = gameState;
      if (gameId === "preview_mode" || hostId !== userId) return;
      const alivePlayers = Object.values(players).filter((p) => p.isAlive);
      if (alivePlayers.length === 0 && Object.keys(players).length > 0) return;
      const aliveMurderers = alivePlayers.filter((p) => p.role === "Murderer");
      const goodTeamCount = alivePlayers.filter(
        (p) => p.role === "Innocent" || p.role === "Detective" || p.role === "Agent" || p.role === "Coroner"
      ).length;
      if (goodTeamCount <= aliveMurderers.length) {
        const victimNames = (gameState.victimIds || [])
          .map((vid) => players[vid]?.name || "ไม่ทราบชื่อ")
          .join(", ");
        const newLogs = [];
        if (victimNames) {
          newLogs.push(`💀 คืนที่ ${gameState.round || 1}: ผู้เล่น [${victimNames}] ถูกพบเป็นศพอย่างมีปริศนา`);
        }
        newLogs.push("🏆 จบคดี: ฝ่ายฆาตกรชนะคดี! เพราะผู้บริสุทธิ์ล้มตายจนไม่เหลือทางต่อสู้");

        await updateDoc(gameDocRef, {
          phase: GAME_PHASES.GAME_OVER,
          winner: "Murderer",
          winReason:
            "ฝ่ายฆาตกรชนะเพราะจำนวนผู้บริสุทธิ์เหลือน้อยกว่าหรือเท่ากับฆาตกร",
          gameLogs: arrayUnion(...newLogs),
        });
      }
    };
    const { phase, victimIds, players } = gameState;
    if (
      phase === GAME_PHASES.ASSISTANT_CONFIRM ||
      phase === GAME_PHASES.DETECTIVE_CONFIRM ||
      phase === GAME_PHASES.MURDERER_COVERUP
    ) {
      const victimsAreDead = (victimIds || []).every(
        (vid) => players[vid] && !players[vid].isAlive
      );
      if (victimsAreDead) checkWinAfterDeath();
    }
  }, [gameState, userId, gameId, gameDocRef]);

  const handleCopyRoomId = () => {
    navigator.clipboard.writeText(gameId);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleShowRoleModal = () => setRoleModalVisible(true);

  // --- Rendering Functions ---
  const renderActionForPlayer = (id, player) => {
    const { phase, votes, gameModes, leadMurdererId } = gameState;

    if (!me?.isAlive) return null;

    // เอเจนต์ที่ยังมีชีวิตอยู่สามารถเลือกปิดปากผู้เล่นอื่นได้
    if (
      phase === GAME_PHASES.AGENT_ACTION &&
      me.role === "Agent" &&
      player.isAlive &&
      !gameState.agentConfirmations?.[userId]
    ) {
      return (
        <HoldToConfirmButton
          data-sound-ignore="true"
          onConfirm={() => handleAgentSilence(id)}
          holdTime={1500}
          className="bg-purple-700 hover:bg-purple-600 text-xs px-2 py-1 rounded text-white animate-pulse-red"
        >
          กดค้างเพื่อปิดปาก
        </HoldToConfirmButton>
      );
    }

    if (
      phase === GAME_PHASES.MURDERER_ACTION &&
      me.role === "Murderer" &&
      userId === leadMurdererId &&
      player.isAlive &&
      id !== userId &&
      !gameModes?.isRandomKillMode
    ) {
      return (
        <HoldToConfirmButton
          data-sound-ignore="true"
          onConfirm={() => handleMurder(id)}
          holdTime={1500}
          className="bg-[#b22222] hover:bg-[#8B0000] text-xs px-2 py-1 rounded text-white disabled:bg-gray-600 disabled:cursor-not-allowed animate-pulse-red"
        >
          กดค้างเพื่อสังหาร
        </HoldToConfirmButton>
      );
    }
    if (phase === GAME_PHASES.VOTING && player.isAlive) {
      const myVote = (votes || {})[userId];
      if (myVote === id)
        return <span className="text-yellow-400 font-bold">โหวตแล้ว</span>;
      if (myVote !== undefined) return null;
      if (id === userId) return null;
      return (
        <HoldToConfirmButton
          data-sound-ignore="true"
          onConfirm={() => handleVote(id)}
          holdTime={1500}
          className="bg-[#8b6b4e] hover:bg-[#725a40] text-xs px-2 py-1 rounded text-white"
        >
          กดค้างเพื่อโหวต
        </HoldToConfirmButton>
      );
    }
    return null;
  };

  const renderCenterPanel = () => {
    const {
      phase,
      round,
      victimIds,
      trueClues,
      fakeClues,
      currentClues,
      countdown,
      shuffledClues,
      gameModes,
      leadMurdererId,
    } = gameState;
    const isMyTurnAsVictim =
      phase === GAME_PHASES.VICTIM_ACTION && (victimIds || []).includes(userId);
    const isMyTurnAsMurderer =
      phase === GAME_PHASES.MURDERER_COVERUP && leadMurdererId === userId;

    if (phase === GAME_PHASES.CORONER_ACTION) {
      const isCoroner = me?.role === "Coroner";
      const isVictim = (victimIds || []).includes(userId);

      if (isCoroner) {
        if (gameState.coronerQuery) {
          return (
            <div className="text-center flex flex-col items-center justify-center h-full animate-fade-in p-4">
              <p className="text-2xl text-purple-400 font-bold mb-4">
                คุณชี้ถามเบาะแสสำเร็จแล้ว
              </p>
              <div className="bg-white p-2 rounded-sm shadow-xl w-48 mb-4 border-4 border-purple-500">
                <img
                  src={gameState.coronerQuery.clueUrl}
                  alt={gameState.coronerQuery.clueName}
                  className="w-full aspect-square object-cover"
                />
                <p className="text-center font-semibold mt-2 text-[#5a4a3a]">
                  {gameState.coronerQuery.clueName}
                </p>
              </div>
              <p className="text-lg text-gray-300 animate-pulse">
                กำลังรอเหยื่อพิจารณาผลชันสูตร...
              </p>
            </div>
          );
        }

        const activeSelectedIds = [
          ...(trueClues || []).map((c) => c.clueId),
          ...(fakeClues || []).map((c) => c.clueId),
        ];
        const coronerClues = (currentClues || []).filter(
          (clue) => !activeSelectedIds.includes(clue.id)
        );

        return (
          <div className="flex flex-col h-full animate-fade-in">
            <h3 className="text-xl font-bold mb-1 pb-2 flex-shrink-0 font-serif text-purple-300 border-b border-purple-500/30">
              ตาแพทย์นิติเวช: เลือกหลักฐานเพื่อชันสูตร (ถามเหยื่อ)
            </h3>
            <p className="mb-3 text-sm text-gray-300">
              เลือกจิ้มเบาะแส 1 ชิ้นจาก 6 ชิ้นที่ไม่ได้อยู่ในคดี เพื่อถามเหยื่อว่าเกี่ยวข้องกับหลักฐานจริงหรือไม่ (คนอื่นจะไม่เห็นคำถาม-คำตอบนี้)
            </p>
            <div className="grid grid-cols-3 gap-2 md:gap-4 flex-grow min-h-0">
              {coronerClues.map((clue) => (
                <button
                  key={clue.id}
                  data-sound-ignore="true"
                  onClick={() => setZoomedClue(clue)}
                  className="p-1.5 pb-2 rounded-sm text-center flex flex-col items-center justify-start transition-transform transform bg-white hover:scale-105 cursor-pointer border-4 border-transparent hover:border-purple-400 overflow-hidden shadow-lg"
                >
                  <img
                    src={clue.url}
                    alt={clue.name}
                    className="w-full aspect-square object-cover"
                  />
                  <p className="text-xs md:text-sm font-semibold mt-1 text-black truncate w-full">
                    {clue.name}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );
      }

      if (isVictim) {
        if (gameState.coronerQuery) {
          const hasResponded = gameState.coronerQuery.response !== null;
          return (
            <div className="w-full h-full flex items-center justify-center p-4 animate-fade-in">
              <div className="bg-[#f3e9d2] text-[#5a4a3a] p-6 sm:p-8 rounded-lg shadow-2xl border-4 border-purple-700 max-w-md w-full text-center">
                <p className="text-purple-700 border-2 border-purple-700 p-1 text-center font-mono text-xl sm:text-2xl font-bold tracking-widest opacity-80 mb-4">
                  CORONER INQUIRY
                </p>
                <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-2">
                  แพทย์นิติเวชสอบปากคำ
                </h2>
                <p className="mb-6 text-sm sm:text-base">
                  แพทย์นิติเวชต้องการวิเคราะห์: <br />
                  <strong className="text-red-700">"{gameState.coronerQuery.clueName}"</strong> เกี่ยวข้องกับหลักฐานจริงของคุณหรือไม่?
                </p>
                <div className="bg-white p-2 rounded-sm shadow-md w-40 mx-auto mb-6">
                  <img
                    src={gameState.coronerQuery.clueUrl}
                    alt={gameState.coronerQuery.clueName}
                    className="w-full aspect-square object-cover"
                  />
                </div>
                {!hasResponded ? (
                  <div className="flex gap-4 justify-center">
                    <button
                      onClick={() => handleCoronerResponse("yes")}
                      className="bg-green-700 hover:bg-green-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg text-lg transform hover:scale-105 active:scale-95 transition-transform"
                    >
                      👍 เกี่ยวข้อง (ใช่)
                    </button>
                    <button
                      onClick={() => handleCoronerResponse("no")}
                      className="bg-red-700 hover:bg-red-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg text-lg transform hover:scale-105 active:scale-95 transition-transform"
                    >
                      👎 ไม่เกี่ยวข้อง
                    </button>
                  </div>
                ) : (
                  <p className="text-green-700 font-bold text-lg">
                    คุณได้ส่งคำตอบให้แพทย์นิติเวชแล้ว
                  </p>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="text-center flex flex-col items-center justify-center h-full animate-pulse">
            <p className="text-2xl text-purple-400 font-bold">
              กำลังรอแพทย์นิติเวชชี้หลักฐานเพื่อชันสูตร...
            </p>
          </div>
        );
      }

      return (
        <div className="text-center flex items-center justify-center h-full">
          <p className="text-2xl animate-pulse text-[#f3e9d2]">
            รอแพทย์นิติเวชและผู้เสียชีวิตชันสูตร...
          </p>
        </div>
      );
    }

    if (phase === GAME_PHASES.AGENT_ACTION && me?.role === "Agent") {
      const myConfirmation = gameState.agentConfirmations?.[userId];
      if (myConfirmation) {
        return (
          <div className="text-center flex items-center justify-center h-full">
            <p className="text-2xl text-purple-400">คุณได้เลือกเป้าหมายแล้ว รอบนี้ <span className="font-bold text-white">{gameState.players[myConfirmation]?.name}</span> จะถูก🤐</p>
          </div>
        );
      }
      return (
        <div className="text-center flex flex-col items-center justify-center h-full">
          <p className="text-3xl font-bold text-purple-400 font-serif">
            ถึงตาคุณปิดปาก
          </p>
          <p className="text-xl mt-4">
            กรุณาเลือกผู้เล่นที่จะปิดปากจากรายชื่อ
          </p>
        </div>
      );
    }

    if (phase === GAME_PHASES.MURDERER_ACTION && me.role === "Murderer") {
      const isLead = userId === leadMurdererId;
      if (gameModes?.isRandomKillMode)
        return (
          <div className="text-center flex items-center justify-center h-full">
            <p className="text-2xl animate-pulse text-[#f3e9d2]">
              กำลังสุ่มเป้าหมาย...
            </p>
          </div>
        );
      return (
        <div className="text-center flex flex-col items-center justify-center h-full">
          {isLead ? (
            <>
              <p className="text-3xl font-bold text-red-500 font-serif">
                ถึงตาคุณสังหาร
              </p>
              <p className="text-xl mt-4">
                กรุณาเลือกผู้เล่นที่จะสังหารจากรายชื่อ
              </p>
            </>
          ) : (
            <p className="text-2xl animate-pulse text-[#f3e9d2]">
              รอฆาตกรหลักดำเนินการ...
            </p>
          )}
        </div>
      );
    }

    if (isMyTurnAsVictim) {
      const iHaveSelectedClue = (trueClues || []).some(
        (c) => c.selectedBy === userId
      );
      return (
        <div className="flex flex-col h-full">
          <div className="bg-red-900/50 border border-red-500 text-white text-center p-2 sm:p-4 rounded-lg mb-4 flex-shrink-0">
            <p className="text-xl sm:text-2xl font-bold font-serif">
              คุณคือเหยื่อ!
            </p>
            {gameState.killersForThisRound && (
              <p className="mt-2 text-base sm:text-lg">
                ฆาตกรที่สังหารคุณคือ{" "}
                <span className="text-yellow-400">
                  {gameState.killersForThisRound
                    .map((id) => gameState.players[id]?.name)
                    .join(" และ ")}
                </span>
              </p>
            )}
            <p className="mt-2 sm:mt-4 font-bold text-lg">
              {iHaveSelectedClue
                ? "คุณได้ทิ้งเบาะแสแล้ว รอเหยื่อคนอื่น..."
                : "เลือกเบาะแส 1 ชิ้นเพื่อทิ้งไว้!"}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 md:gap-4 flex-grow min-h-0">
            {(currentClues || []).map((clue) => (
              <button
                key={clue.id}
                disabled={iHaveSelectedClue}
                onClick={() => !iHaveSelectedClue && setZoomedClue(clue)}
                className={`p-1.5 pb-2 rounded-sm text-center flex flex-col items-center justify-start transition-transform transform relative overflow-hidden bg-white ${!iHaveSelectedClue
                    ? "hover:scale-105 cursor-pointer border-2 border-yellow-400 animate-pulse"
                    : "cursor-default opacity-60"
                  }`}
              >
                <img
                  src={clue.url}
                  alt={clue.name}
                  className="w-full aspect-square object-cover"
                />
                <p className="text-xs md:text-sm font-semibold mt-1 text-black">
                  {clue.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (isMyTurnAsMurderer) {
      const fakeCluesNeeded = 2;
      const myFakeCluesCount = (fakeClues || []).filter(
        (c) => c.selectedBy === userId
      ).length;
      const requiredForMe = 2;
      return (
        <div className="flex flex-col h-full">
          <h3 className="text-xl font-bold mb-1 pb-2 flex-shrink-0 font-serif text-[#f3e9d2]">
            เลือกเบาะแสปลอม (เลือกได้ {requiredForMe} ชิ้น)
          </h3>
          <p className="mb-3 text-gray-300">
            เลือกแล้ว {(fakeClues || []).length}/{fakeCluesNeeded} ชิ้น
            (คุณเลือกแล้ว {myFakeCluesCount}/{requiredForMe})
          </p>
          <div className="grid grid-cols-3 gap-2 md:gap-4 flex-grow min-h-0">
            {(currentClues || []).map((clue) => {
              const isTrueClue = (trueClues || []).some(
                (tc) => tc.clueId === clue.id
              );
              const isFakeClue = (fakeClues || []).some(
                (fc) => fc.clueId === clue.id
              );
              const canClick =
                !isTrueClue && !isFakeClue && myFakeCluesCount < requiredForMe;
              let borderClass = isTrueClue
                ? "border-green-500"
                : canClick
                  ? "border-yellow-400 animate-pulse"
                  : "border-transparent";
              return (
                <button
                  key={clue.id}
                  data-sound-ignore="true"
                  disabled={!canClick}
                  onClick={() => canClick && setZoomedClue(clue)}
                  className={`p-1.5 pb-2 rounded-sm text-center flex flex-col items-center justify-start transition-transform transform relative bg-white border-4 ${borderClass} ${canClick
                      ? "hover:scale-105 cursor-pointer"
                      : "cursor-default"
                    } ${isFakeClue ? "opacity-30" : ""} overflow-hidden`}
                >
                  <img
                    src={clue.url}
                    alt={clue.name}
                    className="w-full aspect-square object-cover"
                  />
                  <p className="text-xs md:text-sm font-semibold mt-1 text-black">
                    {clue.name}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (phase === GAME_PHASES.DISCUSSION || phase === GAME_PHASES.VOTING) {
      const chosenClues = shuffledClues || [];
      const chosenClueIds = chosenClues.map((c) => c.clueId);
      const unchosenClues = (currentClues || []).filter(
        (c) => !chosenClueIds.includes(c.id)
      );
      const rotations = [
        "rotate-1",
        "-rotate-2",
        "rotate-2",
        "-rotate-1",
        "rotate-3",
        "rotate-1",
        "-rotate-1",
        "rotate-2",
        "-rotate-3",
      ];
      return (
        <div
          className="flex flex-col h-full relative p-2 md:p-4 rounded-lg bg-cover bg-center"
          style={{ backgroundImage: 'url("/images/cork_background.jpg")' }}
        >
          {countdown !== null && (
            <div className="absolute top-2 right-2 w-12 h-12 md:w-16 md:h-16 bg-gray-900/80 rounded-full flex items-center justify-center border-2 border-yellow-400 z-20">
              <span className="text-2xl md:text-3xl font-bold text-yellow-400 animate-pulse">
                {countdown}
              </span>
            </div>
          )}
          <div className="flex-shrink-0 text-center mb-2">
            <p className="text-xs sm:text-base text-black bg-white/70 inline-block px-3 py-1 rounded-md shadow-md">
              เหยื่อ:{" "}
              <span className="font-bold text-red-700">
                {(victimIds || [])
                  .map((vid) => gameState.players[vid]?.name)
                  .join(", ")}
              </span>
            </p>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            {me?.role === "Coroner" && gameState.coronerQuery && (
              <div className="mb-3 bg-purple-950/95 text-white p-3 rounded-lg border-2 border-purple-400 shadow-md flex items-center gap-3 animate-fade-in flex-shrink-0">
                <div className="bg-white p-1 rounded-sm w-12 h-12 flex-shrink-0">
                  <img
                    src={gameState.coronerQuery.clueUrl}
                    alt={gameState.coronerQuery.clueName}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-xs font-bold text-purple-300 font-serif">บันทึกนิติเวช (เฉพาะคุณที่เห็น)</p>
                  <p className="text-sm">
                    หลักฐาน <strong className="text-yellow-400">"{gameState.coronerQuery.clueName}"</strong> 👉 เหยื่อชี้ว่า{" "}
                    {gameState.coronerQuery.response === "yes" ? (
                      <span className="text-green-400 font-bold">เกี่ยวข้อง 👍</span>
                    ) : (
                      <span className="text-red-400 font-bold">ไม่เกี่ยวข้อง 👎</span>
                    )}
                  </p>
                </div>
              </div>
            )}
            <div className="mb-2">
              <h3
                className="text-base md:text-xl font-bold mb-2 text-[#f3e9d2] font-serif"
                style={{ textShadow: "1px 1px 2px #5a4a3a" }}
              >
                รอบที่ {round}: หลักฐานในที่เกิดเหตุ
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {chosenClues.map((clue, index) => (
                  <div
                    key={clue.clueId}
                    className={`bg-white p-1 md:p-1.5 rounded-sm shadow-lg text-center transform relative transition-transform hover:scale-105 ${rotations[index]}`}
                    style={{ boxShadow: "2px 2px 8px rgba(0,0,0,0.4)" }}
                  >
                    <img
                      src="/images/pin.png"
                      alt="pin"
                      className="absolute -top-2 md:-top-3 left-1/2 -translate-x-1/2 w-6 h-6 md:w-8 md:h-8 z-10"
                    />
                    <img
                      src={clue.clueUrl}
                      alt={clue.clueName}
                      className="w-full aspect-square object-cover"
                    />
                    <p className="mt-1 text-[10px] md:text-xs text-black truncate px-1">
                      {clue.clueName}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2">
              <h3
                className="text-base md:text-xl font-bold mb-2 text-[#f3e9d2] font-serif"
                style={{ textShadow: "1px 1px 2px #5a4a3a" }}
              >
                หลักฐานที่ไม่ถูกเลือก
              </h3>
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {unchosenClues.map((clue, index) => (
                  <div
                    key={clue.id}
                    className={`bg-white p-1 md:p-1.5 rounded-sm shadow-lg text-center opacity-70 relative transform transition-transform hover:scale-105 ${rotations[index + 3]
                      }`}
                  >
                    <img
                      src={clue.url}
                      alt={clue.name}
                      className="w-full aspect-square object-cover"
                    />
                    <p className="mt-1 text-[10px] md:text-xs text-black truncate px-1">
                      {clue.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {phase === GAME_PHASES.DISCUSSION &&
            (gameState.hostId === userId ? (
              <div className="text-center mt-4 flex-shrink-0">
                <button
                  onClick={handleStartVoting}
                  className="bg-[#556b2f] hover:bg-[#6B8E23] text-white font-bold py-2 px-6 sm:py-3 sm:px-8 rounded-lg text-base sm:text-xl shadow-lg"
                >
                  เริ่มโหวตทันที
                </button>
              </div>
            ) : (
              <p className="text-center mt-4 text-yellow-800 bg-white/70 p-2 rounded-md font-bold animate-pulse flex-shrink-0">
                รอโฮสต์เริ่มการโหวต...
              </p>
            ))}
        </div>
      );
    }

    let waitingText = "รอการดำเนินการจากผู้เล่นอื่น...";
    if (phase === GAME_PHASES.MURDERER_ACTION)
      waitingText = "รอการกระทำจากฆาตกร...";
    else if (phase === GAME_PHASES.VICTIM_ACTION)
      waitingText = "รอการกระทำจากเหยื่อ...";
    else if (phase === GAME_PHASES.MURDERER_COVERUP)
      waitingText = "รอฆาตกรเลือกเบาะแสปลอม...";
    else if (phase === GAME_PHASES.ASSISTANT_CONFIRM)
      waitingText = "รอผู้ช่วยฆาตกรยืนยัน...";
    else if (phase === GAME_PHASES.DETECTIVE_CONFIRM)
      waitingText = "รอนักสืบยืนยัน...";
    else if (phase === GAME_PHASES.AGENT_ACTION)
      waitingText = "รอเอเจนต์ดำเนินการ...";

    return (
      <div className="text-center flex items-center justify-center h-full">
        <p className="text-2xl animate-pulse text-[#f3e9d2]">{waitingText}</p>
      </div>
    );
  };

  const renderContent = () => {
    switch (gameState.phase) {
      case GAME_PHASES.LOBBY:
        const playerCount = Object.keys(gameState.players).length;
        const specialRoles = gameState.specialRoles || {
          assistants: 0,
          detectives: 0,
          extraMurderers: 0,
        };
        const gameModes = gameState.gameModes || DEFAULT_GAME_MODES;
        const isMobile = windowWidth < 768;
        const totalReadyCount =
          Object.keys(gameState.readyPlayers || {}).length + 1;
        const allPlayersReady =
          playerCount > 0 && totalReadyCount === playerCount;

        let gridColsClass = "grid-cols-4";
        let cardWidthClass = "w-[68px]";
        let cardPinClass = "w-[20px] h-[20px] -top-2";
        let cardTextClass = "text-[9px] leading-tight";
        let cardHostIconClass = "w-[16px] h-[16px] -top-1 -right-1";
        let cardAvatarContainerClass = "mb-0.5";
        let cardReadyBadgeClass = "w-[14px] h-[14px] text-[8px] border-[1px]";

        if (playerCount > 8) {
          gridColsClass = "grid-cols-5";
          cardWidthClass = "w-[50px]";
          cardPinClass = "w-[16px] h-[16px] -top-1.5";
          cardTextClass = "text-[8px] leading-tight";
          cardHostIconClass = "w-[14px] h-[14px] -top-0.5 -right-0.5";
          cardAvatarContainerClass = "mb-0";
          cardReadyBadgeClass = "w-[12px] h-[12px] text-[7px] border-[1px]";
        }
        let maxAssistants = 0,
          maxDetectives = 0,
          maxExtraMurderers = 0,
          maxAgents = 0,
          maxCoroners = 0;
        if (playerCount >= 9) {
          maxAssistants = 2;
          maxDetectives = 1;
          maxExtraMurderers = 1;
          maxAgents = 1;
          maxCoroners = 1;
        } else if (playerCount >= 8) {
          maxAssistants = 2;
          maxDetectives = 1;
          maxAgents = 1;
          maxCoroners = 1;
        } else if (playerCount >= 5) {
          maxAssistants = 1;
          maxDetectives = 1;
          maxAgents = 1;
          maxCoroners = 1;
        } else if (playerCount >= 4) {
          maxAgents = 1;
        }

        const RoleCounter = ({ label, role, count, max }) => {
          const isDisabled = max === 0;
          return (
            <div className={`flex items-center justify-between w-full p-2 rounded-md border transition-all ${
              isDisabled 
                ? "bg-gray-300/20 border-gray-400/30 text-gray-400 opacity-40 cursor-not-allowed select-none" 
                : "bg-white/50 border-gray-400 text-[#5a4a3a]"
            }`}>
              <span className="font-semibold">{label}</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => !isDisabled && handleUpdateSpecialRoles(role, -1)}
                  disabled={isDisabled || count === 0}
                  className="w-8 h-8 font-bold text-xl bg-[#b22222]/80 hover:bg-[#b22222] text-white rounded-full disabled:bg-gray-400/50 disabled:text-gray-400 transition-all duration-100 active:scale-95 active:shadow-inner"
                >
                  -
                </button>
                <span className="text-xl font-bold w-4 text-center">
                  {count}
                </span>
                <button
                  onClick={() => !isDisabled && handleUpdateSpecialRoles(role, 1)}
                  disabled={isDisabled || count >= max}
                  className="w-8 h-8 font-bold text-xl bg-[#556b2f]/80 hover:bg-[#556b2f] text-white rounded-full disabled:bg-gray-400/50 disabled:text-gray-400 transition-all duration-100 active:scale-95 active:shadow-inner"
                >
                  +
                </button>
              </div>
            </div>
          );
        };

        return (
          <div className="w-full max-w-[95%] xl:max-w-[90%] 2xl:max-w-[1400px] h-[calc(100dvh-80px)] md:h-auto p-2 sm:p-4 bg-[#4a3a2a] rounded-lg shadow-2xl flex flex-col md:flex-row gap-4 border-4 border-black/30 relative md:items-stretch overflow-hidden">
            <div
              className="w-full md:w-1/2 lg:w-3/5 flex flex-col bg-cover bg-center rounded-md p-2 h-auto max-h-[45vh] md:max-h-none flex-shrink-0 relative overflow-hidden"
              style={{ backgroundImage: 'url("/images/cork_background.jpg")' }}
            >
              <header className="relative flex-shrink-0 flex items-center justify-center p-2 md:p-4">
                <button
                  onClick={() => setIsLeaveConfirmVisible(true)}
                  className="absolute top-4 left-4 md:top-6 md:left-6 z-20 w-12 h-12 bg-red-800/80 hover:bg-red-700 text-white rounded-full shadow-lg border-2 border-white/30 flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                  title="ออกจากห้อง"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19H17a2 2 0 002-2V7a2 2 0 00-2-2h-7m4 7H3m0 0l4-4m-4 4l4 4"
                    />
                  </svg>
                </button>
                <div
                  onClick={handleCopyRoomId}
                  className="relative flex cursor-pointer flex-col items-center transform -rotate-1 z-10 px-3 py-4 md:py-5 w-11/12 max-w-xs md:w-[320px]"
                  style={{
                    backgroundImage: 'url("/images/torn-paper.png")',
                    backgroundSize: "100% 100%",
                    backgroundRepeat: "no-repeat",
                    minHeight: isMobile ? "95px" : "110px",
                  }}
                >
                  <p
                    className={`font-serif font-bold text-[#5a4a3a] ${isMobile ? "text-base" : "text-lg"
                      }`}
                  >
                    ID ห้องสำหรับสืบสวน
                  </p>
                  <span
                    className={`font-typewriter font-bold tracking-widest text-[#6b4f3a] ${isMobile ? "text-3xl" : "text-4xl"
                      }`}
                  >
                    {gameId}
                  </span>
                  <p
                    className={`text-[#8b6b4e] mt-1 ${isMobile ? "text-[10px]" : "text-xs"
                      }`}
                  >
                    (คลิกเพื่อคัดลอก)
                  </p>
                  {isCopied && (
                    <div className="absolute left-1/2 -bottom-10 -translate-x-1/2 rounded-full bg-[#5a4a3a] font-bold text-[#f3e9d2] shadow-lg px-4 py-2 text-xs sm:text-sm copied-balloon-animation whitespace-nowrap z-50">
                      คัดลอกแล้ว!
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShareModalVisible(true)}
                  className="absolute top-4 right-4 md:top-6 md:right-6 z-20 w-12 h-12 bg-[#8b6b4e]/80 hover:bg-[#725a40] text-[#f3e9d2] rounded-full shadow-lg border-2 border-white/30 flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                    />
                  </svg>
                </button>
              </header>
              <main className="flex-grow overflow-y-auto min-h-0 p-4 pb-12 flex flex-col justify-start md:justify-center">
                <div className={`grid ${gridColsClass} md:grid-cols-3 lg:grid-cols-4 gap-x-1 gap-y-2 md:gap-4 md:gap-y-8 w-full mx-auto justify-items-center`}>
                  {Object.values(lobbyPlayers)
                    .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0))
                    .map((p, index) => {
                      const rotations = [
                        "-rotate-2",
                        "rotate-1",
                        "rotate-3",
                        "-rotate-1",
                        "rotate-2",
                        "-rotate-3",
                      ];
                      const rotationClass = rotations[index % rotations.length];
                      let animationClass = "";
                      if (p.status === "entering")
                        animationClass = "animate-card-deal-in";
                      else if (p.status === "exiting")
                        animationClass = "animate-fade-out-down";
                      return (
                        <div
                          key={p.id}
                          className={animationClass}
                          style={{ animationDelay: `${index * 100}ms` }}
                        >
                          <div
                            className={`bg-white p-1 pb-2 rounded-sm ${cardWidthClass} md:w-32 mx-auto relative transform transition-transform hover:scale-110 ${rotationClass}`}
                            style={{
                              boxShadow: "3px 3px 10px rgba(0,0,0,0.5)",
                            }}
                          >
                            <img
                              src="/images/pin.png"
                              alt="pin"
                              className={`absolute ${cardPinClass} md:-top-3 md:w-8 md:h-8 left-1/2 -translate-x-1/2 z-10`}
                            />
                            {p.id === gameState.hostId && (
                              <div
                                className={`absolute ${cardHostIconClass} md:-top-2 md:-right-2 z-10 select-none`}
                                title="โฮสต์ของห้อง"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  className="w-full h-full text-amber-400 filter drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]"
                                >
                                  <path d="M2 21h20v-2H2v2zM22 7l-4 4-6-8-6 8-4-4v10h20V7z" />
                                </svg>
                              </div>
                            )}
                            <div className={`w-full aspect-square bg-gray-300 flex items-center justify-center ${cardAvatarContainerClass}`}>
                              <img
                                src={p.avatar}
                                alt={p.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <p className={`w-full text-center font-semibold ${cardTextClass} md:text-sm text-[#5a4a3a] truncate px-1 flex items-center justify-center gap-1`}>
                              {(p.isGoogle || (p.avatar && p.avatar.includes("googleusercontent.com"))) && <GoogleIcon />}
                              <span>{p.name}</span>
                            </p>
                            {gameState.readyPlayers?.[p.id] && (
                              <div
                                className={`absolute bottom-1 right-1 ${cardReadyBadgeClass} md:w-6 md:h-6 md:text-xs md:border-2 rounded-full bg-green-600 border-white flex items-center justify-center text-white font-bold shadow-md`}
                                title="พร้อมแล้ว"
                              >
                                ✓
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </main>
              <button
                onClick={() => setIsHowToPlayVisible(true)}
                className="absolute bottom-4 right-4 z-20 w-12 h-12 bg-[#8b6b4e]/80 hover:bg-[#725a40] text-[#f3e9d2] font-bold rounded-full shadow-lg text-2xl border-2 border-white/30 flex items-center justify-center transition-transform active:scale-95 active:shadow-inner"
              >
                ?
              </button>
            </div>
            <div className="w-full md:w-1/2 lg:w-2/5 flex flex-col flex-1 overflow-y-auto min-h-0 md:h-auto">
              {gameState.hostId === userId ? (
                <div className="z-10 w-full flex-grow flex flex-col bg-[#f3e9d2] p-4 rounded-lg shadow-2xl text-gray-800 border-4 border-[#c8bda9]">
                  <div className="flex-grow overflow-y-auto pr-2">
                    <h3 className="font-bold font-serif mb-2 text-center text-[#5a4a3a] text-xl">
                      บทบาทพิเศษ
                    </h3>
                    <div className="space-y-2">
                      <RoleCounter
                        label="ผู้ช่วยฆาตกร"
                        role="assistants"
                        count={specialRoles.assistants || 0}
                        max={maxAssistants}
                      />
                      <RoleCounter
                        label="นักสืบ"
                        role="detectives"
                        count={specialRoles.detectives || 0}
                        max={maxDetectives}
                      />
                      <RoleCounter
                        label="แพทย์นิติเวช"
                        role="coroners"
                        count={specialRoles.coroners || 0}
                        max={maxCoroners}
                      />
                      <RoleCounter
                        label="เอเจนต์"
                        role="agents"
                        count={specialRoles.agents || 0}
                        max={maxAgents}
                      />
                      <RoleCounter
                        label="ฆาตกรเพิ่มเติม"
                        role="extraMurderers"
                        count={specialRoles.extraMurderers || 0}
                        max={maxExtraMurderers}
                      />
                    </div>
                    <hr className="border-gray-400/60 my-3" />
                    <h3 className="font-bold font-serif mb-2 text-center text-[#5a4a3a] text-xl">
                      โหมดการเล่น
                    </h3>
                    <div className="space-y-2">
                      <SettingCounterSwitch
                        label="เวลาพูดคุย (วินาที)"
                        description="ค่าเริ่มต้น 60 วินาที"
                        value={gameModes.discussionTime || 60}
                        min={20}
                        max={180}
                        step={10}
                        onUpdate={(newValue) =>
                          handleGameModeChange("discussionTime", newValue)
                        }
                        onReset={() =>
                          handleGameModeChange("discussionTime", 60)
                        }
                      />
                      <ToggleSwitch
                        label="โหมดสุ่มฆ่า"
                        description="ระบบจะสุ่มฆ่าแทนฆาตกร"
                        isEnabled={gameModes.isRandomKillMode}
                        onToggle={(val) =>
                          handleGameModeChange("isRandomKillMode", val)
                        }
                      />
                      <ToggleSwitch
                        label="โหมดมือใหม่"
                        description="เปิดเผยบทบาทของผู้ที่ถูกสังหารทันที"
                        isEnabled={gameModes.isBeginnerMode}
                        onToggle={(val) =>
                          handleGameModeChange("isBeginnerMode", val)
                        }
                      />
                    </div>

                    {/* แนะนำความสมดุลของเกม (Game Balance Tips) */}
                    <div className="mt-4 bg-[#f8f1e5] border border-[#a08d70]/40 rounded-lg p-3 text-xs text-[#5a4a3a] shadow-inner text-left">
                      <p className="font-bold mb-1.5 flex items-center gap-1 text-[#6b4f3a] font-serif">
                        💡 คำแนะนำการจัดสมดุล (โฮสต์)
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700">
                        {playerCount <= 6 ? (
                          <li>
                            <strong className="text-amber-800">ผู้เล่น 5-6 คน:</strong> ระบบบังคับเปิดได้เพียง **นักสืบ** หรือ **แพทย์นิติเวช** ตัวใดตัวหนึ่งเท่านั้น เพื่อไม่ให้ฝั่งคนดีข้อมูลล้นจนไม่สนุก
                          </li>
                        ) : playerCount <= 8 ? (
                          <li>
                            <strong className="text-amber-800">ผู้เล่น 7-8 คน:</strong> แนะนำให้มี **ผู้ช่วยฆาตกรเพียง 1 คน** หากเปิด 2 คน ฝั่งคนร้ายจะกุมเสียงโหวตสภาได้ง่ายเกินไป
                          </li>
                        ) : (
                          <li>
                            <strong className="text-green-800">ผู้เล่น 9+ คน:</strong> เปิดทุกบทบาทพิเศษได้เลยเพื่อความโกลาหลและสนุกสนานสูงสุด!
                          </li>
                        )}
                        <li className="mt-1 opacity-80 list-none italic">
                          * โฮสต์สามารถ ปรับลด/ปิดสเปเชียลโรล เพื่อคุมความยากได้ตามต้องการ
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex-shrink-0 pt-3 text-center">
                    <button
                      onClick={handleStartGame}
                      disabled={playerCount < 3 || !allPlayersReady}
                      className="w-full bg-[#8b6b4e] hover:bg-[#725a40] text-white font-bold py-3 px-8 rounded-lg shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed transition-transform active:scale-95"
                    >{`เริ่มเกม (${totalReadyCount}/${playerCount} คนพร้อม)`}</button>
                    {playerCount < 3 && (
                      <p className="text-red-700 font-semibold text-xs mt-1">
                        {" "}
                        (ต้องการผู้เล่นอย่างน้อย 3 คน)
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="z-10 w-full flex-grow flex flex-col items-center justify-between bg-[#f3e9d2] py-3 px-4 sm:px-8 rounded-lg shadow-2xl text-[#5a4a3a] border-4 border-[#c8bda9]">
                  <div className="w-full flex-grow overflow-y-auto space-y-2 pr-1">
                    <div className="w-full">
                      <h3 className="font-bold font-serif mb-1.5 text-center text-[#5a4a3a] text-lg sm:text-xl">
                        บทบาทพิเศษ
                      </h3>
                      <div className="space-y-1.5">
                        {playerCount < 4 ? (
                          <div className="font-semibold text-center text-xs p-2 text-gray-500 bg-white/30 rounded-md">
                            <span className="text-sm text-gray-500 italic">
                              (ต้องการผู้เล่น 4 คนขึ้นไป)
                            </span>
                          </div>
                        ) : (
                          <>
                            {playerCount >= 5 && specialRoles.assistants > 0 && (
                              <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                                <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                                  ผู้ช่วยฆาตกร (x{specialRoles.assistants})
                                </span>
                                <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                                  รู้เบาะแสจริง 1 ชิ้น แต่ไม่รู้ว่าใครคือฆาตกร
                                </p>
                              </div>
                            )}
                            {playerCount >= 5 && specialRoles.detectives > 0 && (
                              <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                                <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                                  นักสืบ (x{specialRoles.detectives})
                                </span>
                                <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                                  รู้เบาะแสจริง 1 ชิ้น (ก่อนฆาตกรผสมของปลอม)
                                </p>
                              </div>
                            )}
                            {playerCount >= 5 && specialRoles.coroners > 0 && (
                              <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                                <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                                  แพทย์นิติเวช (x{specialRoles.coroners || 0})
                                </span>
                                <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                                  สามารถแอบสืบถามเหยื่อเกี่ยวกับการ์ดหลักฐานว่าเกี่ยวข้องกับคดีหรือไม่
                                </p>
                              </div>
                            )}
                            {playerCount >= 9 &&
                              specialRoles.extraMurderers > 0 && (
                                <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                                  <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                                    ฆาตกรเพิ่มเติม (x
                                    {specialRoles.extraMurderers})
                                  </span>
                                  <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                                    เพิ่มฆาตกรในเกม (เมื่อมีผู้เล่น 9+ คน)
                                  </p>
                                </div>
                              )}
                            {(specialRoles.agents || 0) > 0 && (
                              <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                                <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">เอเจนต์ (x{specialRoles.agents})</span>
                                <p className="text-xs text-gray-600 hidden sm:block mt-0.5">สามารถ "ปิดปาก" ผู้เล่นได้หนึ่งคนต่อรอบ</p>
                              </div>
                            )}
                            {specialRoles.assistants === 0 &&
                              specialRoles.detectives === 0 &&
                              (specialRoles.coroners || 0) === 0 &&
                              (specialRoles.agents || 0) === 0 &&
                              (playerCount < 9 ||
                                specialRoles.extraMurderers === 0) && (
                                <div className="bg-gray-100 p-2 rounded-lg border border-gray-300 text-center">
                                  <span className="text-sm text-gray-500 italic">
                                    ไม่มีบทบาทพิเศษ
                                  </span>
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="w-full">
                      <h3 className="font-bold font-serif mb-1.5 text-center text-[#5a4a3a] text-lg sm:text-xl">
                        โหมดการเล่น
                      </h3>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between w-full p-2 bg-[#e1d4be] rounded-lg border border-[#c8bda9] shadow-inner text-left">
                          <div>
                            <span className="font-semibold text-xs sm:text-sm text-[#5a4a3a]">
                              เวลาพูดคุย (วินาที)
                            </span>
                            <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                              ค่าเริ่มต้น 60 วินาที
                            </p>
                          </div>
                          <span className="text-sm sm:text-lg font-bold text-[#5a4a3a] pr-2">
                            {gameModes.discussionTime || 60}
                          </span>
                        </div>
                        {gameModes.isRandomKillMode && (
                          <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                            <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                              โหมดสุ่มฆ่า
                            </span>
                            <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                              ระบบจะสุ่มฆ่าแทนฆาตกร
                            </p>
                          </div>
                        )}
                        {gameModes.isBeginnerMode && (
                          <div className="bg-[#e1d4be] p-2 rounded-lg border border-[#c8bda9] shadow-inner text-left">
                            <span className="font-bold text-xs sm:text-sm text-[#6b4f3a]">
                              โหมดมือใหม่
                            </span>
                            <p className="text-xs text-gray-600 hidden sm:block mt-0.5">
                              เปิดเผยบทบาทของผู้ที่ถูกสังหารทันที
                            </p>
                          </div>
                        )}
                        {!gameModes.isRandomKillMode &&
                          !gameModes.isSpeedMode &&
                          !gameModes.isBeginnerMode && (
                            <div className="bg-white/30 p-2 rounded-lg border border-gray-300 text-center">
                              <span className="text-sm text-gray-500 italic">
                                ไม่มีโหมดพิเศษ
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="w-full flex-shrink-0 pt-3 border-t border-[#c8bda9] flex flex-col items-center">
                    <button
                      onClick={
                        !gameState.readyPlayers?.[userId]
                          ? handleReady
                          : handleCancelReady
                      }
                      className={`w-3/4 h-12 sm:h-16 rounded-lg text-white font-bold text-xl sm:text-2xl shadow-lg transition-all duration-300 ease-in-out active:scale-95 active:shadow-inner ${gameState.readyPlayers?.[userId]
                          ? "bg-[#a08d70] hover:bg-[#8d7c62]"
                          : "bg-[#556b2f] hover:bg-[#6B8E23]"
                        }`}
                    >
                      <StatusSwitcher
                        text1="🟢 พร้อม"
                        text2="🔴 ยกเลิก"
                        isSwitched={!!gameState.readyPlayers?.[userId]}
                        className="h-full"
                      />
                    </button>
                    <div className="h-6 mt-2 text-sm text-gray-500 text-center w-3/4">
                      <StatusSwitcher
                        text1=""
                        text2="(รอโฮสต์เริ่มเกม...)"
                        isSwitched={!!gameState.readyPlayers?.[userId]}
                        className="!text-sm !font-normal"
                        direction="vertical"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case GAME_PHASES.PRE_GAME_COUNTDOWN:
        return (
          <div className="text-center flex flex-col items-center justify-center h-full">
            <p className="text-3xl text-gray-300 mb-4">เกมจะเริ่มใน...</p>
            <p
              key={gameState.countdown}
              className="text-9xl font-bold text-yellow-400 animate-thump"
            >
              {gameState.countdown}
            </p>
          </div>
        );

      case GAME_PHASES.CLUE_REVEAL:
        const alivePlayers = Object.values(gameState.players).filter(
          (p) => p.isAlive
        );
        const iAmReady = gameState.readyPlayers?.[userId];
        const readyCount = alivePlayers.filter((p) => gameState.readyPlayers?.[p.id]).length;
        const totalAliveCount = alivePlayers.length;
        const rotationsClue = [
          "-rotate-2",
          "rotate-1",
          "rotate-2",
          "-rotate-1",
          "rotate-3",
          "-rotate-3",
          "rotate-1",
          "-rotate-2",
          "rotate-2",
        ];
        return (
          <div className="flex flex-col h-full w-full justify-between md:justify-center gap-2 md:gap-4 py-2 md:py-6 overflow-y-auto">
            <div className="text-center flex-shrink-0">
              <h2 className="text-xl md:text-3xl font-bold text-yellow-300 mb-1 md:mb-4 font-serif">
                เบาะแสรอบที่ {gameState.round}
              </h2>
              <p className="text-xs md:text-lg mb-1">
                ทุกคนโปรดตรวจสอบเบาะแส แล้วกดยืนยันเพื่อดำเนินต่อไป
              </p>
            </div>
            <div className="flex flex-col items-center justify-center px-2">
              <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-2xl w-full mx-auto">
                {(gameState.currentClues || []).map((clue, index) => (
                  <div
                    key={clue.id}
                    className={`bg-white p-1.5 pb-2 rounded-sm shadow-lg text-center flex flex-col items-center justify-center relative transform transition-transform hover:scale-105 ${rotationsClue[index % rotationsClue.length]
                      }`}
                  >
                    <div className="w-full aspect-square bg-gray-800">
                      <img
                        src={clue.url}
                        alt={clue.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="mt-1 text-xs md:text-sm text-black font-semibold truncate w-full px-1">
                      {clue.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-center flex-shrink-0 flex flex-col items-center w-full">
              <div className="w-full max-w-[280px] md:max-w-md mx-auto mb-2 md:mb-4 text-left text-xs md:text-sm">
                <p className="font-bold text-gray-300 mb-1">สถานะผู้เล่น:</p>
                <ul className="bg-black/20 p-2 md:p-3 rounded-md border border-gray-600 space-y-1 max-h-20 md:max-h-24 overflow-y-auto">
                  {alivePlayers.map((player) => (
                    <li
                      key={player.id}
                      className="flex justify-between items-center text-gray-200"
                    >
                      <span
                        className={`flex items-center gap-1 ${
                          player.id === userId ? "font-bold text-amber-400" : ""
                        }`}
                      >
                        {(player.isGoogle || (player.avatar && player.avatar.includes("googleusercontent.com"))) && <GoogleIcon />}
                        <span>{player.name}</span>
                        {player.id === userId && <span className="ml-1">(คุณ)</span>}
                      </span>
                      {gameState.readyPlayers?.[player.id] ? (
                        <span className="text-green-400 font-bold flex items-center gap-1.5">
                          <span className="inline-flex w-5 h-5 rounded-full bg-green-600 border border-white/20 items-center justify-center text-white text-[10px]">
                            ✓
                          </span>
                          พร้อม
                        </span>
                      ) : (
                        <span className="text-yellow-400 animate-pulse">
                          🤔 ยังไม่พร้อม ...
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              {me.isAlive && (
                !iAmReady ? (
                  <button
                    onClick={handleReady}
                    className="bg-[#556b2f] hover:bg-[#6B8E23] text-white font-bold py-2 px-6 md:py-3 md:px-8 rounded-lg text-lg md:text-xl shadow-lg transition-transform active:scale-95"
                  >
                    พร้อม
                  </button>
                ) : (
                  <button
                    disabled
                    className="bg-[#a08d70] text-[#f3e9d2] font-bold py-2 px-6 md:py-3 md:px-8 rounded-lg text-lg md:text-xl shadow-inner cursor-not-allowed"
                  >
                    ✓ ยืนยันแล้ว ({readyCount}/{totalAliveCount} คนพร้อม)
                  </button>
                )
              )}
            </div>
          </div>
        );

      case GAME_PHASES.ROLE_REVEAL:
        if (!me.roleCardImage)
          return (
            <div className="text-center flex flex-col items-center justify-center h-full">
              <p className="text-2xl animate-pulse">กำลังเปิดการ์ด...</p>
            </div>
          );
        const revealedCount = Object.keys(gameState.revealedRoles || {}).length;
        const totalPlayers = Object.values(gameState.players).filter(
          (p) => p.isAlive
        ).length;
        let glowEffectClass = "";
        if (showGlow) {
          switch (me.role) {
            case "Murderer":
              glowEffectClass = "animate-glow-red";
              break;
            case "Assistant":
              glowEffectClass = "animate-glow-orange";
              break;
            case "Detective":
              glowEffectClass = "animate-glow-green";
              break;
            case "Agent":
              glowEffectClass = "animate-glow-green";
              break;
            case "Innocent":
              glowEffectClass = "animate-glow-blue";
              break;
            default:
              glowEffectClass = "";
          }
        }
        return (
          <div className="text-center flex flex-col items-center justify-center h-full">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-[#f3e9d2] font-serif">
              คุณได้รับบทบาทแล้ว!
            </h2>
            <div className={`w-64 sm:w-72 aspect-[2/3] [perspective:1000px]`}>
              <div
                className={`relative w-full h-full transition-transform duration-700 ease-in-out [transform-style:preserve-3d] ${isCardFlipped || hasRevealed
                    ? "[transform:rotateY(180deg)]"
                    : ""
                  }`}
                onClick={handleRevealRole}
              >
                <div
                  className={`absolute w-full h-full [backface-visibility:hidden] rounded-lg overflow-hidden ${glowEffectClass}`}
                >
                  <img
                    src="/images/role_cards/card_back.png"
                    alt="Card Back"
                    className="w-full h-full object-contain cursor-pointer"
                  />
                </div>
                <div
                  className={`absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg overflow-hidden ${glowEffectClass}`}
                >
                  <img
                    src={me.roleCardImage}
                    alt={`Role card ${me.role}`}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
            {!hasRevealed ? (
              <p className="mt-8 text-2xl text-yellow-400 animate-pulse">
                คลิกที่การ์ดเพื่อดูบทบาทของคุณ
              </p>
            ) : (
              <p className="mt-8 text-xl text-green-400">
                รอผู้เล่นคนอื่น... ({revealedCount}/{totalPlayers})
              </p>
            )}
          </div>
        );

      case GAME_PHASES.NIGHT_TRANSITION:
        return (
          <div className="text-center flex flex-col items-center justify-center h-full animate-fade-in-out-night">
            <p className="text-9xl mb-4">😴</p>
            <h2 className="text-3xl sm:text-5xl font-bold text-blue-300 font-serif">
              เข้าสู่ช่วงเวลากลางคืน
            </h2>
            {!isVoiceMuted && (
              <p className="text-2xl text-gray-400 mt-2">ทุกคนโปรดหลับตา...</p>
            )}
          </div>
        );

      case GAME_PHASES.DAY_TRANSITION:
        return (
          <div className="text-center flex flex-col items-center justify-center h-full animate-fade-in-out-day">
            <p className="text-9xl mb-4">☀️</p>
            <h2 className="text-3xl sm:text-5xl font-bold text-yellow-300 font-serif">
              เข้าสู่ช่วงเวลากลางวัน
            </h2>
            {!isVoiceMuted && (
              <p className="text-2xl text-gray-400 mt-2">ทุกคนลืมตาได้...</p>
            )}
          </div>
        );

      case GAME_PHASES.ASSISTANT_CONFIRM:
      case GAME_PHASES.DETECTIVE_CONFIRM:
        const isAssistantPhase =
          gameState.phase === GAME_PHASES.ASSISTANT_CONFIRM;
        const relevantRole = isAssistantPhase ? "Assistant" : "Detective";
        const isMyTurn = me.role === relevantRole;
        if (isMyTurn) {
          const title = isAssistantPhase ? "ถึงตาผู้ช่วยฆาตกร" : "ถึงตานักสืบ";
          const description = isAssistantPhase
            ? "เหยื่อได้ทิ้งเบาะแสไว้ โปรดตรวจสอบและกดยืนยัน"
            : "คุณได้รับเบาะแสจริงจากที่เกิดเหตุ โปรดตรวจสอบและกดยืนยัน";
          const hasConfirmed = gameState.confirmations?.[userId];
          return (
            <div className="w-full h-full flex items-center justify-center p-4">
              <div className="bg-[#f3e9d2] text-[#5a4a3a] p-6 sm:p-8 rounded-lg shadow-2xl border-4 border-gray-700 max-w-md w-full animate-fade-in">
                <p className="text-red-700 border-2 border-red-700 p-1 text-center font-mono text-xl sm:text-2xl font-bold tracking-widest opacity-80 mb-4">
                  CONFIDENTIAL
                </p>
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-center mb-2">
                  {title}
                </h2>
                <p className="text-center mb-6">{description}</p>
                <div className="space-y-4">
                  {(gameState.trueClues || []).map((clue, index) => (
                    <div
                      key={index}
                      className="bg-white p-2 rounded-sm shadow-xl transform -rotate-2 mx-auto w-52 sm:w-64"
                    >
                      <div className="border-2 border-gray-200">
                        <img
                          src={clue.clueUrl}
                          alt={clue.clueName}
                          className="w-full aspect-square object-cover"
                        />
                      </div>
                      <p className="text-center font-semibold mt-2 text-base sm:text-lg">
                        {clue.clueName}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  {!hasConfirmed ? (
                    <button
                      data-sound-ignore="true"
                      onClick={
                        isAssistantPhase
                          ? handleAssistantConfirm
                          : handleDetectiveConfirm
                      }
                      className="bg-[#8b6b4e] hover:bg-[#725a40] text-white font-bold py-3 px-8 rounded-lg shadow-lg text-lg transform hover:scale-105 transition-transform"
                    >
                      ยืนยันการรับรู้
                    </button>
                  ) : (
                    <p className="text-green-700 font-bold text-lg">
                      คุณได้ยืนยันข้อมูลแล้ว
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        } else {
          const waitingText = isAssistantPhase
            ? "รอผู้ช่วยฆาตกรดำเนินการ..."
            : "รอนักสืบดำเนินการ...";
          return (
            <div className="text-center flex items-center justify-center h-full">
              <p className="text-2xl animate-pulse text-[#f3e9d2]">
                {waitingText}
              </p>
            </div>
          );
        }

      case GAME_PHASES.VOTE_RESULT:
        const eliminatedPlayer = gameState.eliminatedPlayerId
          ? gameState.players[gameState.eliminatedPlayerId]
          : null;
        return (
          <div className="w-full h-full flex flex-col items-center justify-center text-center p-4 overflow-hidden relative">
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.8)_70%)] z-0"></div>
            <h2 className="text-3xl sm:text-5xl font-bold mb-8 text-[#f3e9d2] font-serif z-10 drop-shadow-md">
              ผลการโหวต
            </h2>
            {eliminatedPlayer ? (
              <div className="flex flex-col items-center z-10">
                <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-44 md:h-44 rounded-full border-4 sm:border-8 border-[#f3e9d2] bg-[#e1d4be] overflow-hidden shadow-[0_0_30px_rgba(255,255,255,0.3)] mb-6 animate-spotlight-rise">
                  <img
                    src={eliminatedPlayer.avatar}
                    alt={eliminatedPlayer.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <p
                  className="text-2xl sm:text-3xl mb-2 text-white animate-fade-in"
                  style={{ animationDelay: "0.8s" }}
                >
                  <span className="font-bold text-yellow-400 text-4xl block mb-2">
                    {eliminatedPlayer.name}
                  </span>
                  ถูกโหวตให้ออกจากเกม!
                </p>
              </div>
            ) : (
              <div className="z-10 animate-bounce">
                <p className="text-6xl mb-4">⚖️</p>
                <p className="text-2xl sm:text-3xl text-gray-300">
                  คะแนนโหวตเสมอกัน <br />
                  <span className="text-white font-bold">ไม่มีใครถูกกำจัด</span>
                </p>
              </div>
            )}
            <div className="mt-12 z-10">
              {gameState.hostId === userId ? (
                <button
                  onClick={handleProceedAfterVote}
                  className="bg-[#8b6b4e] hover:bg-[#725a40] text-white font-bold py-3 px-12 rounded-full text-xl shadow-lg transform transition hover:scale-110 active:scale-95"
                >
                  ดำเนินการต่อ
                </button>
              ) : (
                <p className="text-xl text-green-400 animate-pulse bg-black/50 px-4 py-2 rounded-full">
                  รอโฮสต์ดำเนินการต่อ...
                </p>
              )}
            </div>
          </div>
        );

      case GAME_PHASES.GAME_OVER:
        const isWinner =
          (gameState.winner === "Innocent" &&
            me.role !== "Murderer" &&
            me.role !== "Assistant") ||
          (gameState.winner === "Murderer" &&
            (me.role === "Murderer" || me.role === "Assistant"));
        const allMurderers = Object.values(gameState.players).filter(
          (p) => p.role === "Murderer"
        );
        return (
          <div
            className={`absolute inset-0 overflow-y-auto text-center flex flex-col items-center ${!isWinner ? "vignette-effect" : ""
              }`}
          >
            <div className="animate-game-over-swoop z-10 pt-4 px-2 sm:px-4 flex flex-col items-center">
              <img
                src="/logo.png"
                alt="Game Logo"
                className="h-16 sm:h-20 md:h-24 object-contain mb-4 select-none pointer-events-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]"
              />
              <h2 className="text-3xl sm:text-4xl font-bold mb-2 font-serif text-[#f3e9d2]">
                จบเกม!
              </h2>
              <p
                className={`text-5xl sm:text-6xl font-extrabold ${gameState.winner === "Murderer"
                    ? "text-red-500"
                    : "text-blue-400"
                  }`}
              >
                ฝ่าย{" "}
                {gameState.winner === "Murderer" ? "ฆาตกร" : "ผู้บริสุทธิ์"}{" "}
                ชนะ!
              </p>
              {gameState.winReason && (
                <p className="mt-2 text-base sm:text-lg text-gray-300">
                  {gameState.winReason}
                </p>
              )}
              <p className="mt-2 sm:mt-4 text-lg sm:text-xl">
                ฆาตกรคือ: {allMurderers.map((m) => m.name).join(", ")}
              </p>
            </div>
            <div className="w-full max-w-4xl mt-4 space-y-4 z-10 px-2 sm:px-4">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-3 border-b border-gray-600 pb-2 text-[#f3e9d2] font-serif">
                  สรุปบทบาท
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {Object.values(gameState.players).map((p) => {
                    let rColor = "text-blue-400";
                    if (p.role === "Murderer") rColor = "text-red-400";
                    if (p.role === "Assistant") rColor = "text-orange-400";
                    if (p.role === "AssistantRepentant") rColor = "text-amber-500 font-semibold";
                    if (p.role === "Detective") rColor = "text-green-400";
                    return (
                      <div
                        key={p.id}
                        className="relative bg-gray-700/50 p-3 rounded-lg text-center flex flex-col items-center"
                      >
                        <div className="relative w-24 h-36 mx-auto">
                          {p.roleCardImage && (
                            <img
                              src={p.roleCardImage}
                              alt={p.role}
                              className={`absolute inset-0 w-full h-full object-cover rounded-md ${!p.isAlive ? "grayscale opacity-70" : ""
                                }`}
                            />
                          )}
                          {!p.isAlive && (
                            <>
                              <div className="absolute inset-0 bg-black/30 rounded-md z-10 pointer-events-none"></div>
                              <div className="absolute top-1/2 left-1/2 z-20 pointer-events-none">
                                <span className="block text-red-700 font-bold text-xl border-4 border-red-700 px-2 py-1 font-serif rounded-sm bg-white/10 animate-stamp-slam whitespace-nowrap">
                                  ถูกกำจัด
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        <div
                          className={`relative w-12 h-12 rounded-full border-2 border-[#8b6b4e] bg-[#e1d4be] overflow-hidden drop-shadow-lg mb-2 ${!p.isAlive ? "grayscale opacity-70" : ""
                            } -mt-6 -translate-x-8`}
                        >
                          <img
                            src={p.avatar}
                            alt={p.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <div>
                            <p className="font-semibold text-sm sm:text-base">
                              {p.name}
                            </p>
                            {(p.id === gameState.hostId || p.id === userId) && (
                              <p className="text-xs text-yellow-400 font-normal">
                                ({p.id === gameState.hostId && "โฮสต์"}
                                {p.id === gameState.hostId &&
                                  p.id === userId &&
                                  ", "}
                                {p.id === userId && "คุณ"})
                              </p>
                            )}
                          </div>
                          <p className={`text-sm ${rColor}`}>
                            {ROLE_NAMES_TH[p.role] || p.role}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {(gameState.evidenceBoard || []).length > 0 && (
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold mb-3 border-b border-gray-600 pb-2 text-[#f3e9d2] font-serif">
                    เฉลยหลักฐาน
                  </h3>
                  <div className="space-y-4">
                    {gameState.evidenceBoard.map((e) => {
                      const trueCluesList = e.trueClues || [];
                      const fakeCluesList = e.fakeClues || [];
                      // วางหลักฐานจริงอยู่ตรงกลาง (ชิ้นที่ 2) เสมอ
                      const orderedClues = [];
                      if (fakeCluesList[0]) orderedClues.push(fakeCluesList[0]);
                      if (trueCluesList[0]) orderedClues.push(trueCluesList[0]);
                      if (fakeCluesList[1]) orderedClues.push(fakeCluesList[1]);
                      // ป้องกันการตกหล่น
                      fakeCluesList.slice(2).forEach((c) => orderedClues.push(c));
                      trueCluesList.slice(1).forEach((c) => orderedClues.push(c));

                      return (
                        <div
                          key={e.round}
                          className="bg-[#2a1c12]/95 border border-[#5a4a3a]/40 p-4 rounded-lg shadow-xl mb-4 text-[#f3e9d2]"
                        >
                          <p className="font-bold mb-3 text-sm sm:text-base text-[#f3e9d2] font-serif border-b border-white/10 pb-1.5 flex items-center justify-between">
                            <span>รอบที่ {e.round}</span>
                            <span className="text-xs text-gray-400 font-sans font-normal">เหยื่อ: {e.victim}</span>
                          </p>
                          <div className="grid grid-cols-3 gap-4 items-center py-2 px-1">
                            {orderedClues.map((clue) => {
                              const isTrueClue = (e.trueClues || []).some(
                                (tc) => tc.clueId === clue.clueId
                              );
                              return (
                                <div
                                  key={clue.clueId}
                                  className={`bg-white p-1 rounded-sm shadow-md border-4 transition-all duration-300 ${
                                    isTrueClue
                                      ? "border-green-500 scale-105 sm:scale-110 z-10 shadow-[0_6px_15px_rgba(0,0,0,0.45)] ring-4 ring-green-500/20"
                                      : "border-red-500 scale-95 opacity-80"
                                  }`}
                                >
                                  <img
                                    src={clue.clueUrl}
                                    alt={clue.clueName}
                                    title={clue.clueName}
                                    className="w-full aspect-square object-cover"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 mb-8 flex flex-col sm:flex-row gap-4 items-center justify-center z-10">
              {gameState.hostId === userId && (
                <>
                  <button
                    onClick={handlePlayAgain}
                    className="bg-[#556b2f] hover:bg-[#6B8E23] text-white font-bold py-3 px-8 rounded-lg text-xl shadow-lg transition-transform active:scale-95"
                  >
                    เริ่มเกมใหม่
                  </button>
                  <button
                    onClick={handleReturnToLobby}
                    className="bg-[#8b6b4e] hover:bg-[#725a40] text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transition-transform active:scale-95"
                  >
                    กลับไปที่ล็อบบี้
                  </button>
                </>
              )}
              {gameState.hostId !== userId && (
                <p className="text-yellow-400 animate-pulse">
                  รอโฮสต์เริ่มเกมใหม่...
                </p>
              )}
              <button
                onClick={onLeaveRoom}
                className="bg-[#a08d70] hover:bg-[#8d7c62] text-white font-bold py-3 px-8 rounded-lg text-lg shadow-lg transition-transform active:scale-95"
              >
                ออกจากห้อง
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="w-full h-full max-w-[95%] xl:max-w-[90%] 2xl:max-w-[1400px] flex flex-col md:flex-row gap-4">
            {zoomedClue && (
              <ClueModal
                clue={zoomedClue}
                onClose={() => setZoomedClue(null)}
                onConfirm={() => {
                  if (gameState.phase === GAME_PHASES.VICTIM_ACTION)
                    handleSelectTrueClue(zoomedClue);
                  else if (gameState.phase === GAME_PHASES.MURDERER_COVERUP)
                    handleSelectFakeClue(zoomedClue);
                  else if (gameState.phase === GAME_PHASES.CORONER_ACTION && me?.role === "Coroner")
                    handleCoronerQuery(zoomedClue);
                  setZoomedClue(null);
                }}
              />
            )}
            <div
              className="md:w-1/3 lg:w-1/4 bg-[#f3e9d2] text-[#5a4a3a] p-4 rounded-lg border-2 border-[#c8bda9] overflow-y-auto"
              style={{
                backgroundImage:
                  "linear-gradient(rgba(90, 74, 58, 0.2) 1px, transparent 1px)",
                backgroundSize: "100% 1.8rem",
                boxShadow: "inset 0 0 10px rgba(0,0,0,0.2)",
              }}
            >
              <h3 className="text-xl font-bold mb-3 border-b border-[#c8bda9] pb-2 text-[#5a4a3a] font-serif">
                รายชื่อผู้ต้องสงสัย
              </h3>
              <ul>
                {Object.entries(gameState.players)
                  .sort(([, a], [, b]) => (a.joinedAt || 0) - (b.joinedAt || 0))
                  .map(([id, player]) => {
                    const revealedRole = gameState.publiclyRevealedRoles?.[id];
                    return (
                      <li
                        key={id}
                        className={`relative flex items-center justify-between p-2 rounded mb-2 transition-colors ${!player.isAlive ? "opacity-60" : ""
                          } ${gameState.votes?.[userId] === id
                            ? "bg-yellow-500/20"
                            : ""
                          }`}
                      >
                        <div className="flex items-center">
                          {/* *** ส่วนที่แก้ไข 2: ย้ายตราประทับมาทับรูป *** */}
                          <div className="relative mr-3 flex-shrink-0">
                            {id === gameState.hostId && (
                              <div
                                className="absolute -top-2.5 -right-2 z-20 select-none pointer-events-none"
                                title="โฮสต์"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  className="w-6 h-6 text-amber-500 filter drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
                                >
                                  <path d="M2 21h20v-2H2v2zM22 7l-4 4-6-8-6 8-4-4v10h20V7z" />
                                </svg>
                              </div>
                            )}
                            <div
                              className={`w-12 h-12 rounded-sm border-4 border-white bg-[#e1d4be] overflow-hidden shadow-md ${!player.isAlive ? "grayscale" : ""
                                }`}
                            >
                              <img
                                src={player.avatar}
                                alt={player.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            {/* ตราประทับ (แสดงเฉพาะตอนตาย) */}
                            {!player.isAlive && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                <span className="text-red-700 font-bold text-[10px] border-2 border-red-700 px-1 py-0.5 transform -rotate-12 bg-white/80 font-serif rounded-sm whitespace-nowrap">
                                  ถูกกำจัด
                                </span>
                              </div>
                            )}
                            {/* ไอคอนถูกปิดปาก (แสดงบนรูปอวาตาร์) */}
                            {(gameState.silencedPlayerIds || []).includes(id) && (
                              <div className="absolute bottom-0 right-0 pointer-events-none z-20 translate-x-1/4 translate-y-1/4">
                                <span className="text-xl drop-shadow-lg" title="ถูกเอเจนต์ปิดปาก">🤐</span>
                              </div>
                            )}
                          </div>
                          {/* *************************************** */}

                          <div>
                            <span
                              className={`inline-flex items-center gap-1 ${id === userId ? "font-bold text-amber-600" : ""
                                } ${!player.isAlive
                                  ? "line-through decoration-red-500 decoration-2"
                                  : ""
                                }`}
                            >
                              {(player.isGoogle || (player.avatar && player.avatar.includes("googleusercontent.com"))) && <GoogleIcon />}
                              <span>{player.name}</span>
                            </span>
                            {revealedRole && !player.isAlive && (
                              <span className="text-xs text-gray-400 ml-1">
                                ({ROLE_NAMES_TH[revealedRole] || revealedRole})
                              </span>
                            )}
                            {gameState.phase === GAME_PHASES.VOTING &&
                              player.isAlive && (
                                <span className="text-lg ml-2">
                                  {gameState.votes?.[id] ? (
                                    <span
                                      className="inline-flex w-5 h-5 rounded-full bg-green-600 border border-white/20 items-center justify-center text-white text-[10px] shadow-sm ml-2"
                                      title="โหวตแล้ว"
                                    >
                                      ✓
                                    </span>
                                  ) : (
                                    <span
                                      title="กำลังคิด..."
                                      className="animate-pulse"
                                    >
                                      🤔
                                    </span>
                                  )}
                                </span>
                              )}
                          </div>
                          {gameState.phase === GAME_PHASES.VOTING &&
                            voteCounts[id] > 0 && (
                              <span className="ml-2 text-yellow-400 text-lg">
                                {"•".repeat(voteCounts[id])}
                              </span>
                            )}
                        </div>
                        {renderActionForPlayer(id, player)}
                      </li>
                    );
                  })}
              </ul>
            </div>
            <div className="flex-1 bg-black/40 backdrop-blur-sm p-4 rounded-lg flex flex-col border border-gray-600">
              {/* Persistent Chat Log: Village News Board */}
              {shouldShowNewsButton && (
                <div className="mb-4 bg-black/30 border border-white/10 rounded-lg shadow-inner p-2 sm:p-3 text-left w-full flex-shrink-0">
                  <div className="flex items-center justify-between border-b border-white/10 pb-1 mb-1.5">
                    <span className="font-bold text-yellow-400 text-xs sm:text-sm flex items-center gap-1 select-none font-serif">
                      📜 ข่าวสารหมู่บ้าน
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-gray-400 font-mono select-none">BULLETIN BOARD</span>
                  </div>
                  <div
                    ref={logContainerRef}
                    className="overflow-y-auto max-h-[80px] sm:max-h-[100px] space-y-1.5 scrollbar-thin scrollbar-thumb-white/10 pr-1"
                  >
                    {!gameState.gameLogs || gameState.gameLogs.length === 0 ? (
                      <p className="text-gray-400 text-xs italic text-center py-1 font-sans">
                        ยังไม่มีข่าวสารในขณะนี้...
                      </p>
                    ) : (
                      gameState.gameLogs.map((log, index) => {
                        let colorClass = "text-white/85";
                        if (log.includes("💀")) colorClass = "text-red-300 font-semibold";
                        else if (log.includes("🤐")) colorClass = "text-amber-300";
                        else if (log.includes("🗳️")) colorClass = "text-sky-300";
                        else if (log.includes("🏆")) colorClass = "text-green-300 font-bold drop-shadow-sm";
                        else if (log.includes("🕊️")) colorClass = "text-emerald-300";

                        return (
                          <p
                            key={index}
                            className={`text-[10px] sm:text-xs leading-relaxed border-b border-white/5 pb-1 font-sans ${colorClass}`}
                          >
                            {log}
                          </p>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
              {renderCenterPanel()}
            </div>
          </div>
        );
    }
  };

  const shouldShowRoleButton =
    me &&
    me.role &&
    gameState.phase !== GAME_PHASES.LOBBY &&
    gameState.phase !== GAME_PHASES.PRE_GAME_COUNTDOWN &&
    gameState.phase !== GAME_PHASES.ROLE_REVEAL &&
    gameState.phase !== GAME_PHASES.GAME_OVER;
  const shouldShowHistoryButton =
    (gameState.evidenceBoard || []).length > 0 &&
    gameState.phase !== GAME_PHASES.LOBBY &&
    gameState.phase !== GAME_PHASES.PRE_GAME_COUNTDOWN &&
    gameState.phase !== GAME_PHASES.ROLE_REVEAL &&
    gameState.phase !== GAME_PHASES.GAME_OVER;
  const shouldShowNewsButton =
    gameState.phase !== GAME_PHASES.LOBBY &&
    gameState.phase !== GAME_PHASES.PRE_GAME_COUNTDOWN &&
    gameState.phase !== GAME_PHASES.ROLE_REVEAL;

  const isLobby = gameState.phase === GAME_PHASES.LOBBY;
  const isClueReveal = gameState.phase === GAME_PHASES.CLUE_REVEAL;
  const shouldRestrictHeight = isLobby || isClueReveal;

  return (
    <div
      className={`w-full text-white flex flex-col items-center justify-center bg-cover bg-center relative overflow-x-hidden ${
        shouldRestrictHeight
          ? "h-screen h-[100dvh] md:min-h-screen md:h-auto overflow-hidden p-2 pt-14 pb-2 sm:p-6 sm:pt-16 sm:pb-6"
          : "min-h-screen p-2 pt-16 pb-20 sm:p-6 sm:pt-16 sm:pb-6"
      }`}
      style={{ backgroundImage: `url("/images/game_background.jpg")` }}
    >
      {/* Game Logo */}
      {gameState.phase !== GAME_PHASES.ROLE_REVEAL && gameState.phase !== GAME_PHASES.GAME_OVER && (
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10 select-none pointer-events-none flex-shrink-0">
          <img
            src="/logo.png"
            alt="Game Logo"
            className="h-10 sm:h-12 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          />
        </div>
      )}
      {vignetteEffect === "murderer" && (
        <div className="vignette-effect-murderer"></div>
      )}
      {vignetteEffect === "victim" && (
        <div className="vignette-effect-victim"></div>
      )}
      {vignetteEffect === "agent" && (
        <div className="vignette-effect-agent"></div>
      )}
      {showTimesUpEffect && (
        <div className="fixed inset-0 z-[9995] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/times_up_effect.gif?t=${Date.now()}`}
            alt="Time's Up!"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showMurdererEffect && (
        <div className="fixed inset-0 z-[9994] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/murderer_effect.gif?t=${Date.now()}`}
            alt="Murderer's Turn"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      <img
        src="/images/coffee-stain.png"
        alt="Coffee Stain Prop"
        className="absolute bottom-4 right-4 w-24 h-24 opacity-70 pointer-events-none z-0"
      />
      {showVictimEffect && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
          <img
            src={`/images/victim_effect.gif?t=${Date.now()}`}
            alt="You are the victim"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showEliminatedEffect && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/eliminated_effect.gif?t=${Date.now()}`}
            alt="You have been eliminated"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showAssistantEffect && (
        <div className="fixed inset-0 z-[9997] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/assistant_effect.gif?t=${Date.now()}`}
            alt="Assistant's turn"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showCoronerEffect && (
        <div className="fixed inset-0 z-[9996] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/coroner_effect.gif?t=${Date.now()}`}
            alt="Coroner's turn"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showDetectiveEffect && (
        <div className="fixed inset-0 z-[9996] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/detective_effect.gif?t=${Date.now()}`}
            alt="Detective's turn"
            className="max-w-full max-h-full"
          />
        </div>
      )}
      {showAgentEffect && (
        <div className="fixed inset-0 z-[9995] flex items-center justify-center bg-black bg-opacity-70">
          <img
            src={`/images/agent_effect.gif?t=${Date.now()}`}
            alt="Agent's turn"
            className="max-w-full max-h-full"
          />
        </div>
      )}

      {!me ? (
        <div className="flex-grow flex items-center justify-center h-full">
          <p>กำลังโหลดเกม...</p>
        </div>
      ) : (
        renderContent()
      )}

      {me && gameState.phase === GAME_PHASES.GAME_OVER && (() => {
        const isWinnerOverlay =
          (gameState.winner === "Innocent" &&
            me.role !== "Murderer" &&
            me.role !== "Assistant") ||
          (gameState.winner === "Murderer" &&
            (me.role === "Murderer" || me.role === "Assistant"));
        return (
          <div className="fixed inset-0 pointer-events-none z-0">
            {isWinnerOverlay
              ? <Confetti width={window.innerWidth} height={window.innerHeight} />
              : <FallingLeaves />}
          </div>
        );
      })()}

      {isHowToPlayVisible && (
        <HowToPlayModal onClose={() => setIsHowToPlayVisible(false)} />
      )}
      {isShareModalVisible && (
        <ShareModal
          gameId={gameId}
          onClose={() => setShareModalVisible(false)}
        />
      )}
      {isLeaveConfirmVisible && (
        <LeaveRoomConfirmModal
          onClose={() => setIsLeaveConfirmVisible(false)}
          onConfirm={() => {
            setIsLeaveConfirmVisible(false);
            onLeaveRoom();
          }}
        />
      )}


      {/* Ghost Button for Dead Players */}
      {!me.isAlive && gameState.phase !== GAME_PHASES.GAME_OVER && (
        <div className="fixed bottom-20 left-4 z-50">
          <button
            onClick={handleSendGhost}
            className="bg-[#5a4a3a] hover:bg-[#725a40] text-[#f3e9d2] rounded-full p-3 shadow-xl border-2 border-[#c8bda9] transition-transform active:scale-90"
            title="หลอกหลอน"
          >
            <span className="text-2xl filter drop-shadow-md">👻</span>
          </button>
        </div>
      )}

      {shouldShowRoleButton && (
        <div className="fixed bottom-4 left-4 z-40">
          <button
            data-sound-ignore="true"
            onClick={handleShowRoleModal}
            className="bg-[#5a4a3a]/80 hover:bg-[#5a4a3a] border-2 border-[#c8bda9] text-[#f3e9d2] font-bold py-2 px-4 rounded-lg shadow-lg transition-transform hover:scale-105 active:scale-95 text-sm sm:text-base"
          >
            ตรวจสอบบทบาท
          </button>
          {isRoleModalVisible && (
            <RoleModal
              role={me.role}
              roleCardImage={me.roleCardImage}
              onClose={() => setRoleModalVisible(false)}
              playSoundQueue={playSoundQueue}
            />
          )}
        </div>
      )}
      {shouldShowHistoryButton && (
        <div className="fixed bottom-4 right-4 z-40">
          <button
            data-sound-ignore="true"
            onClick={() => {
              playSoundQueue({ sounds: "paper_flip", category: "ui" });
              setHistoryModalVisible(true);
            }}
            className="bg-[#5a4a3a]/80 hover:bg-[#5a4a3a] border-2 border-[#c8bda9] text-[#f3e9d2] font-bold py-2 px-4 rounded-lg shadow-lg transition-transform hover:scale-105 active:scale-95 text-sm sm:text-base"
          >
            ประวัติเบาะแส
          </button>
          {isHistoryModalVisible && (
            <EvidenceHistoryModal
              evidenceBoard={gameState.evidenceBoard}
              onClose={() => setHistoryModalVisible(false)}
            />
          )}
        </div>
      )}


      {/* Ghost Container */}
      <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden">
        {ghostsList.map((ghost) => (
          <div
            key={ghost.id}
            className="absolute text-6xl animate-float-ghost"
            style={{ left: `${ghost.left}%`, bottom: '-100px' }}
            onAnimationEnd={() => handleGhostAnimationEnd(ghost.id)}
          >
            {ghost.emoji}
          </div>
        ))}
      </div>




      <div className="fixed top-4 right-4 z-40">
        <button
          onClick={onOpenSettings}
          className="bg-gray-800/70 hover:bg-gray-700 text-white p-3 rounded-full shadow-lg transition-transform hover:scale-110 active:scale-95"
          title="ตั้งค่าเกม"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.096 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
